import messages from '../../statics/messages.json';
import receiveContext from '../../context/ReceiveContext';
import { useState, useEffect, useRef, useCallback } from 'react';
import countriesData from '../../statics/countries.json';
import shipmentData from '../../statics/shipmentsData.json';
import statuses from '../../statics/status.json';
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import * as XLSX from 'xlsx';
import { Modal, Button, Form } from 'react-bootstrap';

import { POPartsData, savePOComment, SearchPOData, SearchVendorData, saveLineStatus, savePOLineStatus, savePOLineTag, savePOFolderUrl } from '../../context/CommonContex';
import HoverPO from '../HoverPO';
import Hover from '../Hover';
import { Link } from 'react-router-dom';
import ColumnVisibilityModal from '../Common/ColumnVisibilityModal';
import PresenceBar from '../Common/PresenceBar';
import CellPresence from '../Common/CellPresence';
import usePresence from '../../hooks/usePresence';
import useFocusedCell from '../../hooks/useFocusedCell';
import { cellKey } from '../../utils/presenceKeys';
import { extractCommentText } from '../../utils/commentText';

import editIcon from '../../statics/images/pencil.png'
import filterIcon from '../../statics/images/filter.png'
import excelIcon from '../../statics/images/excel.png'

// Line Tag Tags with colors
const LINE_TAG_TAGS = [
    { label: 'Hot', color: '#E73030' },
    { label: 'Cable', color: '#6BAEFA' },
    { label: 'Connector', color: '#6BAEFA' },
    { label: 'Consumable', color: '#6BAEFA' },
    { label: 'COTS', color: '#6BAEFA' },
    { label: 'CSM', color: '#D373A8' },
    { label: 'Tools', color: '#F7AC21' },
    { label: 'Equipment', color: '#F7AC21' }
];

// Line Status Tags with colors
const LINE_STATUS_TAGS = [
    { label: 'Ordered', color: '#4AB2D5' },
    { label: 'Out for Delivery', color: '#37E292' },
    { label: 'Delivered', color: '#2D895F' },
    { label: 'Testing', color: '#84E6F8' },
    { label: 'Quality Hold', color: '#EB4F4F' },
    { label: 'Received', color: '#6FB169' }
];

const OPEN_PO_COLUMN_VISIBILITY_STORAGE_KEY = 'openPO_columnVisibility';

/** Presence room id for this page (matches the route path). */
const PRESENCE_PAGE = 'receivingproduction';

const Production = () => {
    const { productionDetailsFeatch, VendorFeatch, updatePOArchivePoLineItem } = receiveContext();
    const productionDataRef = useRef([]);
    // Live presence: who else is on this page / in which cell.
    const presence = usePresence(PRESENCE_PAGE, {
        onLockDenied: (evt) =>
            console.info(`[presence] ${evt?.owner?.name || 'Someone'} is already editing ${evt?.cell}`),
        onRemoteSave: (evt) => applyRemoteSave(evt),
    });

    const poLineCell = useCallback(
        (lineId) => cellKey(PRESENCE_PAGE, 'line', lineId, 'lineStatus'),
        []
    );

    // Notes live in the collapsed grid row, so their presence has to work
    // without anyone expanding the PO line.
    const notesCell = useCallback(
        (lineId) => cellKey(PRESENCE_PAGE, 'line', lineId, 'comment'),
        []
    );

    // Destructured so these identities only change when the roster does, not on
    // every render of this (very large) grid.
    const {
        usersInCell: presenceUsersInCell,
        lockOwner: presenceLockOwner,
        reportFocus: presenceFocus,
        reportBlur: presenceBlur,
    } = presence;

    const poLineLockOwner = useCallback(
        (lineId) => presenceLockOwner(poLineCell(lineId)),
        [presenceLockOwner, poLineCell]
    );

    const renderPoLinePresence = useCallback(
        (lineId) => {
            const key = poLineCell(lineId);
            return (
                <CellPresence
                    users={presenceUsersInCell(key)}
                    owner={presenceLockOwner(key)}
                    compact
                    activity="on this line status"
                />
            );
        },
        [presenceUsersInCell, presenceLockOwner, poLineCell]
    );

    const renderNotesPresence = useCallback(
        (lineId) => {
            const key = notesCell(lineId);
            return (
                <CellPresence
                    users={presenceUsersInCell(key)}
                    size={16}
                    compact
                    activity="in the notes"
                />
            );
        },
        [presenceUsersInCell, notesCell]
    );

    // Several comment chats can be open at once. Each one keeps its own presence.
    const [openNotesLineIds, setOpenNotesLineIds] = useState(() => new Set());

    useEffect(() => {
        const keys = [...openNotesLineIds].map((id) => notesCell(id));
        keys.forEach((key) => presenceFocus(key, { lock: false }));
        return () => {
            keys.forEach((key) => presenceBlur(key));
        };
    }, [openNotesLineIds, notesCell, presenceFocus, presenceBlur]);

    /** Wire one row's notes popup to presence: show who has it open, without a lock. */
    const handleNotesVisibility = useCallback((lineId, isVisible) => {
        const id = String(lineId);
        setOpenNotesLineIds((prev) => {
            if (isVisible) {
                if (prev.has(id)) return prev;
                const next = new Set(prev);
                next.add(id);
                return next;
            }
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);
    const [commentsMap, setCommentsMap] = useState({});
    const handleAddComment = (lineId, newCommentObj, status = 'pending') => {
        const commentToStore =
            newCommentObj && typeof newCommentObj === 'object' && (newCommentObj.comment || newCommentObj.id)
                ? { ...newCommentObj, comment: extractCommentText(newCommentObj), status: newCommentObj.status || status }
                : {
                    id: Date.now(),
                    comment: typeof newCommentObj === 'string' ? newCommentObj : '',
                    lastEditedBy: localStorage.getItem('userEmail') || 'Current User',
                    dateStr: new Date().toLocaleDateString(),
                    timeStr: new Date().toLocaleTimeString(),
                    resolved: false,
                    status,
                };
        setCommentsMap(prev => {
            const current = prev[lineId] || [];
            const filtered = current.filter(c => String(c?.id) !== String(commentToStore.id));
            return { ...prev, [lineId]: [...filtered, commentToStore] };
        });
        const line = (Array.isArray(productionDataRef.current) ? productionDataRef.current : [])
            .find((row) => String(row?.id) === String(lineId));
        presence.broadcastSave({
            cell: notesCell(lineId),
            kind: 'comment',
            poLineId: String(lineId),
            lineId: String(lineId),
            orderNumber: line?.po?.ponum || line?.ponum || '',
            customerName: line?.po?.vendorName || line?.vendorName || '',
            value: commentToStore,
        });
    };
    const handleDeleteComment = (lineId, commentId) => {
        setCommentsMap(prev => ({
            ...prev,
            [lineId]: (prev[lineId] || []).filter(c => String(c?.id) !== String(commentId))
        }));
    };
    const [productionData, setProductionData] = useState([]);
    productionDataRef.current = productionData;
    const [totalRecords, setTotalRecords] = useState(0);
    const [overallTotalRecords, setOverallTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(999999); // Default to "All"
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [statusFilter, setStatusFilter] = useState('Open');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
    const [sortingRequired, setSortRequired] = useState(false);
    const [priorityMap, setPriorityMap] = useState({});
    const [daysFilter, setDaysFilter] = useState(0);
    const [useEffectprcPart, setUseEffectPrcPart] = useState('');
    const [prcPart, setPrcPart] = useState([]);
    const [prcPartValue, setPrcPartValue] = useState('');

    const [useEffectPO, setUseEffectPO] = useState('');
    const [PO, setPO] = useState([]);
    const [POValue, setPOValue] = useState('');

    const [useEffectVendor, setUseEffectVendor] = useState('');
    const [Vendor, setVendor] = useState([]);
    const [VendorValue, setVendorValue] = useState('');

    const [POLinesData, setPoLinesData] = useState(true);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Client-side filter state (filter panel fields not sent to API)
    const [filterAssignedOwner, setFilterAssignedOwner] = useState('');
    const [filterLineNo, setFilterLineNo] = useState('');
    const [filterQty, setFilterQty] = useState('');
    const [filterVendorPart, setFilterVendorPart] = useState('');
    const [filterLineTag, setFilterLineTag] = useState('');
    const [filterPOComment, setFilterPOComment] = useState('');
    const [filterNotes, setFilterNotes] = useState('');
    const [filterLineStatus, setFilterLineStatus] = useState('');

    const [exports, setExport] = useState(false);
    const [exportsClicked, setExportClicked] = useState('');

    const [maxAllSelect, setMaxAllSelect] = useState(false);

    // State for new purchase orders
    const [newPurchaseOrdersData, setNewPurchaseOrdersData] = useState([]);

    // State for archived PO lines (stored as Set of PO line IDs for filtering)
    const [archivedPOLineIds, setArchivedPOLineIds] = useState(new Set());

    // State for archived PO lines data (stored as array of full PO line objects)
    const [archivedPOLinesData, setArchivedPOLinesData] = useState([]);

    // New Purchase Orders section collapse (default expanded, no persistence)
    const [newPurchaseOrdersExpanded, setNewPurchaseOrdersExpanded] = useState(true);

    // Line Tag state management
    const [lineTagSelections, setLineTagSelections] = useState({});
    const [submittedLineTags, setSubmittedLineTags] = useState({});
    const [lineTagsToDeselect, setLineTagsToDeselect] = useState({});
    const [lineTagDropdownOpenId, setLineTagDropdownOpenId] = useState(null);
    const [lineTagDropdownPosition, setLineTagDropdownPosition] = useState(null);

    // Line Status state management
    const [lineStatusSelections, setLineStatusSelections] = useState({});
    const [submittedLineStatuses, setSubmittedLineStatuses] = useState({});
    const [lineStatusTagsToDeselect, setLineStatusTagsToDeselect] = useState({});
    const [lineStatusDropdownOpenId, setLineStatusDropdownOpenId] = useState(null);
    const [lineStatusDropdownPosition, setLineStatusDropdownPosition] = useState(null);

    // Part# column — compact link menu (UI only; actions wired later)
    const [partLinkMenuLineId, setPartLinkMenuLineId] = useState(null);

    // State for expanded PO comments
    const [expandedPOComments, setExpandedPOComments] = useState(new Set());


    // Column visibility state
    const [showColumnModal, setShowColumnModal] = useState(false);
    const [columnVisibility, setColumnVisibility] = useState(() => {
        try {
            const saved = localStorage.getItem(OPEN_PO_COLUMN_VISIBILITY_STORAGE_KEY);
            return saved ? JSON.parse(saved) : {};
        } catch (error) {
            console.error('Failed to read Open PO column visibility:', error);
            return {};
        }
    });

    // Docs column - folder links per PO line
    const [docsLinks, setDocsLinks] = useState({});
    const [showDocsModal, setShowDocsModal] = useState(false);
    const [selectedLineForDocs, setSelectedLineForDocs] = useState(null);
    const [newDocsLinkUrl, setNewDocsLinkUrl] = useState('');

    // PO Access URLs state
    const [poAccessUrls, setPoAccessUrls] = useState({});
    const [newPoAccessUrlName, setNewPoAccessUrlName] = useState('');
    const [newPoAccessUrl, setNewPoAccessUrl] = useState('');

    // Define columns for Open PO
    const openPOColumns = [
        { key: 'assigned_owner', label: 'Assigned / Owner' },
        { key: 'po_label', label: messages.po_label },
        { key: 'line_no', label: 'Line#' },
        { key: 'vendor_label', label: messages.vendor_label },
        { key: 'part', label: 'Part#' },
        { key: 'qty', label: 'QTY' },
        { key: 'vendor_part', label: 'Vendor Part#' },
        { key: 'dock_date', label: 'Dock Date' },
        { key: 'line_tag', label: 'Line Tag' },
        { key: 'comments_po', label: messages.comments_po_label },
        { key: 'comments', label: messages.comments_label },
        { key: 'line_status', label: 'Line Status' },
        { key: 'docs', label: 'Docs' },
        { key: 'archive', label: 'Archive' }
    ];

    const handleColumnVisibilityChange = (newVisibility) => {
        setColumnVisibility(newVisibility);
        try {
            localStorage.setItem(OPEN_PO_COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(newVisibility));
        } catch (error) {
            console.error('Failed to save Open PO column visibility:', error);
        }
    };

    const isColumnVisible = (columnKey) => {
        return columnVisibility[columnKey] !== false; // Default to visible if not set
    };

    const [formData, setFormData] = useState({
        comment: "",
        flowName: "Order",
        status: "",
        poLineId: "",
        poStatus:""
    });



    const [show, setShow] = useState(false);
    const handleShow = () => setShow(true);
    const handleClose = () => setShow(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        debugger
        try {
            formData.id = null;
            formData.flowName = "Purchase Order";
            formData.lastEditedBy = "Test";
            formData.poLineId = updatePopUp.id;
            const comment = await savePOComment(formData);
            const savedLine = updatePopUp && typeof updatePopUp === 'object' ? updatePopUp : null;
            const savedLineId = formData.poLineId || savedLine?.id;
            if (savedLineId) {
                presence.broadcastSave({
                    kind: 'comment',
                    poLineId: String(savedLineId),
                    lineId: String(savedLineId),
                    orderNumber: savedLine?.po?.ponum || '',
                    customerName: savedLine?.po?.vendorName || '',
                    value: {
                        id: comment?.id || Date.now(),
                        comment: formData.comment,
                        lastEditedBy: localStorage.getItem('userEmail') || localStorage.getItem('userName') || 'Current User',
                        dateStr: new Date().toLocaleDateString(),
                        timeStr: new Date().toLocaleTimeString(),
                        status: formData.status || 'pending',
                        resolved: false,
                    },
                });
            }
            setCurrentPage(1);
            fetchProduction((currentPage - 1) * itemsPerPage, itemsPerPage, searchTerm)
            handleClose();
        } catch (error) {
            console.error("Error submitting form:", error);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Toggle function for expanding/collapsing PO comments
    const togglePOComment = (poLineId) => {
        setExpandedPOComments(prev => {
            const newSet = new Set(prev);
            if (newSet.has(poLineId)) {
                newSet.delete(poLineId);
            } else {
                newSet.add(poLineId);
            }
            return newSet;
        });
    };

    // Helper function to check if a comment needs expansion (is long)
    const isCommentLong = (comment) => {
        if (!comment || comment.trim() === '') return false;
        // Check if comment would exceed ~60px height (roughly 3-4 lines at 12px font)
        return comment.length > 150;
    };

    const [updatePopUp, setUpdatePopUp] = useState('');
    const [clear,setClear] = useState(false);
    const fetchProduction = async (index, maxRecords, search, status) => {
        setLoading(true);
        if (status === undefined)
            status = statusFilter;

        // if(!sortingRequired)
        //     production = await productionDetailsFeatch(index, maxRecords, searchTerm, status,daysFilter,null,null);
        // else
        if (new Date(startDate) > new Date(endDate)) {
            alert('Start date can\'t be greater than End date.');
            return;
        } else if (endDate && !startDate) {
            alert('Select Start Date.');
            return;
        }

        try {
            console.log('=== FETCH PRODUCTION START ===');
            console.log('Request Params:', {
                index,
                maxRecords,
                searchTerm,
                status,
                daysFilter,
                sortColumn: sortConfig.key,
                sortDirection: sortConfig.direction,
                POLinesData,
                POValue,
                prcPartValue,
                VendorValue,
                startDate,
                endDate,
                exports
            });

            // CALL 1: Fetch NEW ORDERS only
            console.log('📞 CALL 1: Fetching NEW ORDERS (newOrders=true)...');
            const newOrdersResponse = await productionDetailsFeatch(
                index, 
                maxRecords, 
                searchTerm, 
                status, 
                daysFilter, 
                sortConfig.key, 
                sortConfig.direction, 
                POLinesData, 
                POValue, 
                prcPartValue, 
                VendorValue, 
                startDate, 
                endDate, 
                exports,
                'true'  // newOrders parameter = true
            );
            console.log('✅ NEW ORDERS Response:', {
                totalRecords: newOrdersResponse?.totalRecords,
                dataCount: newOrdersResponse?.production?.length,
                data: newOrdersResponse
            });

            // CALL 2: Fetch ALL ORDERS (excluding new orders)
            console.log('📞 CALL 2: Fetching ALL ORDERS (newOrders=false)...');
            const allOrdersResponse = await productionDetailsFeatch(
                index, 
                maxRecords, 
                searchTerm, 
                status, 
                daysFilter, 
                sortConfig.key, 
                sortConfig.direction, 
                POLinesData, 
                POValue, 
                prcPartValue, 
                VendorValue, 
                startDate, 
                endDate, 
                exports,
                'false'  // newOrders parameter = false
            );
            console.log('✅ ALL ORDERS Response:', {
                totalRecords: allOrdersResponse?.totalRecords,
                dataCount: allOrdersResponse?.production?.length,
                data: allOrdersResponse
            });

            if (maxAllSelect) {
                // Combine both results for "All" view
                const allData = [...(newOrdersResponse.production || []), ...(allOrdersResponse.production || [])];
                console.log('📊 MAX ALL SELECT: Combined data length:', allData.length);
                setProductionData(allData);
                setTotalRecords(newOrdersResponse.totalRecords + allOrdersResponse.totalRecords);
                if ((newOrdersResponse.totalRecords + allOrdersResponse.totalRecords) > 0) {
                    setOverallTotalRecords((prev) => Math.max(prev, newOrdersResponse.totalRecords + allOrdersResponse.totalRecords));
                }
                
                // Initialize PO access URLs for maxAllSelect case
                const urlsMap = {};
                allData.forEach(line => {
                    if (line.po?.purchaseOrderUrls && Array.isArray(line.po.purchaseOrderUrls)) {
                        urlsMap[line.id] = line.po.purchaseOrderUrls;
                    }
                });
                if (Object.keys(urlsMap).length > 0) {
                    setPoAccessUrls(urlsMap);
                }
            }
            else if (exports) {
                // Export both new and all orders
                const allExportData = [...(newOrdersResponse.production || []), ...(allOrdersResponse.production || [])];
                const exportData = allExportData.map((line, lineIndex) => ({
                    PONumber: line.po.ponum,
                    LineNum: line.linenum,
                    VendorName: line.po.vendorName,
                    Part: line.prcpart.substring(3) || '',
                    DueDate: line.duedate ? line.duedate : '',
                    Status: line.status,
                }));

                // Create a worksheet
                const ws = XLSX.utils.json_to_sheet(exportData);

                // Create a workbook
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Data');

                // Export the workbook
                XLSX.writeFile(wb, 'po_export.xlsx');
                setExport(false);
            } else {
                // Normal flow - set data from separate API calls
                console.log('📋 SETTING STATE:');
                console.log('  - New Purchase Orders:', newOrdersResponse.production?.length || 0, 'items');
                console.log('  - All Orders:', allOrdersResponse.production?.length || 0, 'items');
                setNewPurchaseOrdersData(newOrdersResponse.production || []);
                setProductionData(allOrdersResponse.production || []);
                setTotalRecords(allOrdersResponse.totalRecords);
                if (allOrdersResponse.totalRecords > 0) {
                    setOverallTotalRecords((prev) => Math.max(prev, allOrdersResponse.totalRecords));
                }
                
                // Process lineItemStatus from API response and populate submittedLineStatuses state
                const statusMap = {};
                const tagMap = {};
                const urlsMap = {};
                const allData = [...(newOrdersResponse.production || []), ...(allOrdersResponse.production || [])];
                allData.forEach(line => {
                    if (line.lineItemStatus) {
                        try {
                            const parsedStatus = typeof line.lineItemStatus === 'string' 
                                ? JSON.parse(line.lineItemStatus) 
                                : line.lineItemStatus;
                            if (Array.isArray(parsedStatus)) {
                                statusMap[line.id] = parsedStatus;
                            }
                        } catch (e) {
                            console.error(`Error parsing lineItemStatus for line ${line.id}:`, e);
                        }
                    }
                    if (line.lineItemTag) {
                        try {
                            const parsedTags = typeof line.lineItemTag === 'string' 
                                ? JSON.parse(line.lineItemTag) 
                                : line.lineItemTag;
                            if (Array.isArray(parsedTags)) {
                                tagMap[line.id] = parsedTags;
                            }
                        } catch (e) {
                            console.error(`Error parsing lineItemTag for line ${line.id}:`, e);
                        }
                    }
                    // Process PO access URLs from API response
                    if (line.po?.purchaseOrderUrls && Array.isArray(line.po.purchaseOrderUrls)) {
                        urlsMap[line.id] = line.po.purchaseOrderUrls;
                    }
                });
                if (Object.keys(statusMap).length > 0) {
                    setSubmittedLineStatuses(statusMap);
                    console.log('✅ Line status data loaded from API:', statusMap);
                }
                if (Object.keys(tagMap).length > 0) {
                    setSubmittedLineTags(tagMap);
                    console.log('✅ Line tag data loaded from API:', tagMap);
                }
                if (Object.keys(urlsMap).length > 0) {
                    setPoAccessUrls(urlsMap);
                    console.log('✅ PO access URLs loaded from API:', urlsMap);
                }
                console.log('✅ State updated successfully');
            }
        } catch (error) {
            console.error('❌ ERROR fetching production data:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                response: error.response
            });
        }
        
        console.log('=== FETCH PRODUCTION END ===');
        setLoading(false);

    };

    useEffect(() => {
        fetchProduction((currentPage - 1) * itemsPerPage, itemsPerPage, statusFilter);
    }, [currentPage, itemsPerPage, statusFilter, daysFilter, sortConfig, POLinesData, exportsClicked]);


    // Archived data is now managed in state only, no localStorage persistence

    useEffect(() => {
    if (clear) {
            setSearchTerm('');
            setStartDate('');
            setEndDate('');
            setStatusFilter('Open');
            setDaysFilter(0);
            setSortConfig({
                key:null,
                direction:null
            });
            setPoLinesData(true);
            setPOValue('');
            setPrcPartValue('');
            setPOValue('');
            setVendorValue('');
            setFilterAssignedOwner('');
            setFilterLineNo('');
            setFilterQty('');
            setFilterVendorPart('');
            setFilterLineTag('');
            setFilterPOComment('');
            setFilterNotes('');
            setFilterLineStatus('');
            setExport(false);
            setClear(false);
            fetchProduction((currentPage - 1) * itemsPerPage, itemsPerPage, statusFilter);
    }
}, [clear]);

    // Client-side filter: match line against filter panel values (non-API filters)
    const matchesClientFilters = (line) => {
        const assignedOwner = (line.po?.assignedTo ?? line.po?.owner ?? line.assigned_owner ?? '').toString();
        if (filterAssignedOwner.trim() && !assignedOwner.toLowerCase().includes(filterAssignedOwner.trim().toLowerCase())) return false;
        if (filterLineNo.trim() && !String(line.linenum ?? '').toLowerCase().includes(filterLineNo.trim().toLowerCase())) return false;
        if (filterQty.trim() && !String(line.orderqty ?? '').toLowerCase().includes(filterQty.trim().toLowerCase())) return false;
        if (filterVendorPart.trim() && !String(line.cross_part ?? '').toLowerCase().includes(filterVendorPart.trim().toLowerCase())) return false;
        const lineTagLabels = (submittedLineTags[line.id] || []).map(t => t.label).join(' ');
        if (filterLineTag.trim() && !lineTagLabels.toLowerCase().includes(filterLineTag.trim().toLowerCase())) return false;
        const poComment = (line.po?.comment ?? '').toString();
        if (filterPOComment.trim() && !poComment.toLowerCase().includes(filterPOComment.trim().toLowerCase())) return false;
        const lastNote = line.comment?.length ? (line.comment[line.comment.length - 1]?.comment ?? '') : '';
        if (filterNotes.trim() && !lastNote.toLowerCase().includes(filterNotes.trim().toLowerCase())) return false;
        const lineStatusLabels = (submittedLineStatuses[line.id] || []).map(t => t.label).join(' ');
        if (filterLineStatus.trim() && !lineStatusLabels.toLowerCase().includes(filterLineStatus.trim().toLowerCase())) return false;
        return true;
    };

    const filteredProductionData = productionData.filter(line => !archivedPOLineIds.has(line.id)).filter(matchesClientFilters);
    const filteredNewOrdersData = newPurchaseOrdersData.filter(line => !archivedPOLineIds.has(line.id)).filter(matchesClientFilters);

    const totalPages = Math.ceil(totalRecords / itemsPerPage);

    const hasPaginatedRecords = filteredProductionData.length > 0;
    const startRecord = hasPaginatedRecords ? (itemsPerPage >= 999999 ? 1 : (currentPage - 1) * itemsPerPage + 1) : 0;
    const endRecord = hasPaginatedRecords ? startRecord + filteredProductionData.length - 1 : 0;
    const displayTotalRecords = overallTotalRecords > 0 ? overallTotalRecords : totalRecords;

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages)
            return;
        setCurrentPage(page);
    };

    const getVendorName = (id) => {
        const ven = vendors.find((ven) => ven.vennum == id);
        return ven ? ven.name : 'N/A';
    };


    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
        setSortRequired(true);

    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'ascending' ? '▲' : '▼';
    };


    const handlePriorityChange = (lineId, newPriority) => {
        setPriorityMap(prev => ({
            ...prev,
            [lineId]: newPriority
        }));
    };

    // Docs links are managed in state only, no localStorage persistence

    // Docs column - add/open folder links (no API, localStorage only)
    const handleDocsClick = (line) => {
        setSelectedLineForDocs(line);
        setShowDocsModal(true);
        setNewDocsLinkUrl('');
        setNewPoAccessUrl('');
        setNewPoAccessUrlName('');
    };

    const handleAddDocsLink = async () => {
        if (!newDocsLinkUrl.trim()) {
            alert('Please enter a URL');
            return;
        }
        try {
            new URL(newDocsLinkUrl.trim());
        } catch {
            alert('Please enter a valid URL (include http:// or https://)');
            return;
        }
        
        const newLink = { id: Date.now(), url: newDocsLinkUrl.trim(), dateAdded: new Date().toLocaleDateString() };
        
        try {
            // Call backend API to save folder URL
            console.log('🔵 Saving PO Folder URL to backend:', {
                poLineId: selectedLineForDocs.id,
                url: newLink.url
            });
            
            const response = await savePOFolderUrl({
                poLineId: selectedLineForDocs.id,
                url: newLink.url
            });
            
            console.log('🟢 PO Folder URL saved successfully:', response);
            
            // Update local state only after successful backend save
            setDocsLinks(prev => ({
                ...prev,
                [selectedLineForDocs.id]: [...(prev[selectedLineForDocs.id] || []), newLink]
            }));
            setNewDocsLinkUrl('');
        } catch (error) {
            console.error('🔴 Error saving PO Folder URL:', error);
            alert('Failed to save folder URL. Please try again.');
        }
    };

    const handleDeleteDocsLink = (lineId, linkId) => {
        setDocsLinks(prev => {
            const current = prev[lineId] || [];
            return { ...prev, [lineId]: current.filter(link => link.id !== linkId) };
        });
    };

    const handleCloseDocsModal = () => {
        setShowDocsModal(false);
        setSelectedLineForDocs(null);
        setNewDocsLinkUrl('');
        setNewPoAccessUrl('');
        setNewPoAccessUrlName('');
    };

    // PO Access URL handlers
    const handleAddPoAccessUrl = async () => {
        if (!newPoAccessUrlName.trim()) {
            alert('Please enter a URL name');
            return;
        }
        if (!newPoAccessUrl.trim()) {
            alert('Please enter a URL');
            return;
        }
        try {
            new URL(newPoAccessUrl.trim());
        } catch {
            alert('Please enter a valid URL (include http:// or https://)');
            return;
        }

        try {
            const { managePurchaseOrderUrl } = await import('../../context/CommonContex');
            
            const requestData = {
                action: 'add',
                poId: selectedLineForDocs.po.id,
                url: newPoAccessUrl.trim(),
                urlName: newPoAccessUrlName.trim(),
                description: ''
            };

            console.log('🔵 Adding PO Access URL:', requestData);
            const response = await managePurchaseOrderUrl(requestData);
            
            if (response) {
                console.log('🟢 PO Access URL added successfully:', response);
                // Extract the actual URL object from nested response
                const savedUrl = response.data || response;
                setPoAccessUrls(prev => ({
                    ...prev,
                    [selectedLineForDocs.id]: [...(prev[selectedLineForDocs.id] || []), savedUrl]
                }));
                setNewPoAccessUrl('');
                setNewPoAccessUrlName('');
                alert('URL added successfully');
            }
        } catch (error) {
            console.error('🔴 Error adding PO Access URL:', error);
            alert('Failed to add PO Access URL. Please try again.');
        }
    };

    const handleDeletePoAccessUrl = async (lineId, urlId, poId) => {
        try {
            const { managePurchaseOrderUrl } = await import('../../context/CommonContex');
            const requestData = {
                action: 'delete',
                poId: poId,
                id: urlId
            };

            console.log('🔵 Deleting PO Access URL:', requestData);
            const response = await managePurchaseOrderUrl(requestData);
            
            if (response) {
                console.log('🟢 PO Access URL deleted successfully');
                setPoAccessUrls(prev => {
                    const current = prev[lineId] || [];
                    return { ...prev, [lineId]: current.filter(url => url.id !== urlId) };
                });
                alert('URL deleted successfully');
            }
        } catch (error) {
            console.error('🔴 Error deleting PO Access URL:', error);
            alert('Failed to delete PO Access URL. Please try again.');
        }
    };

    // Archive functions
    const handleArchiveOrder = async (line) => {
        if (window.confirm(`Are you sure you want to archive PO ${line.po?.ponum} - Line ${line.linenum}?`)) {
            // Optimistic update on UI
            setArchivedPOLineIds(prev => {
                const newSet = new Set(prev);
                newSet.add(line.id);
                return newSet;
            });

            setArchivedPOLinesData(prev => {
                const existingIndex = prev.findIndex(archived => archived.id === line.id);
                if (existingIndex === -1) {
                    return [...prev, line];
                } else {
                    const updated = [...prev];
                    updated[existingIndex] = line;
                    return updated;
                }
            });

            try {
                const result = await updatePOArchivePoLineItem(line.po?.ponum, line.id, true);

                if (!result.success) {
                    throw new Error(result.message || 'Archive API reported failure');
                }

                console.log('Production archive API success', result);
            } catch (err) {
                console.error('Error archiving PO line item', err);
                // Rollback UI changes
                setArchivedPOLineIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(line.id);
                    return newSet;
                });
                setArchivedPOLinesData(prev => prev.filter(archived => archived.id !== line.id));
                alert('Archive failed: ' + (err.message || 'unknown error'));
            }
        }
    };

    // Line Tag handler functions (only one dropdown open at a time)
    const toggleLineTagDropdown = (lineId, event) => {
        if (lineTagDropdownOpenId === lineId) {
            setLineTagDropdownOpenId(null);
            setLineTagDropdownPosition(null);
            return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        const parentRect = event.currentTarget.closest('.line-tag-dropdown-container')?.getBoundingClientRect() || rect;
        setLineTagDropdownOpenId(lineId);
        setLineTagDropdownPosition({
            top: rect.bottom - parentRect.top,
            left: rect.left - parentRect.left,
            width: rect.width
        });
    };

    const handleLineTagToggle = (lineId, tag) => {
        setLineTagSelections(prev => {
            const currentSelections = prev[lineId] || [];
            const isSelected = currentSelections.some(selection => selection.label === tag.label);
            
            if (isSelected) {
                return {
                    ...prev,
                    [lineId]: currentSelections.filter(selection => selection.label !== tag.label)
                };
            } else {
                return {
                    ...prev,
                    [lineId]: [...currentSelections, tag]
                };
            }
        });
    };

    const handleLineTagDeselect = (lineId, tag) => {
        setLineTagsToDeselect(prev => {
            const currentDeselections = prev[lineId] || [];
            const isMarked = currentDeselections.some(t => t.label === tag.label);
            
            if (isMarked) {
                return {
                    ...prev,
                    [lineId]: currentDeselections.filter(t => t.label !== tag.label)
                };
            } else {
                return {
                    ...prev,
                    [lineId]: [...currentDeselections, tag]
                };
            }
        });
    };

    const hasLineTagChangesToSubmit = (lineId) => {
        const currentSelections = lineTagSelections[lineId] || [];
        const submittedSelections = submittedLineTags[lineId] || [];
        const tagsToRemove = lineTagsToDeselect[lineId] || [];
        
        return currentSelections.length > 0 || tagsToRemove.length > 0;
    };

    const handleLineTagSubmit = async (lineId) => {
        const currentSelections = lineTagSelections[lineId] || [];
        const submittedSelections = submittedLineTags[lineId] || [];
        const tagsToRemove = lineTagsToDeselect[lineId] || [];
        
        // Calculate final selections
        const finalSelections = [
            ...currentSelections,
            ...submittedSelections.filter(
                tag => !tagsToRemove.some(removeTag => removeTag.label === tag.label)
            )
        ].filter((tag, index, array) =>
            array.findIndex(t => t.label === tag.label) === index
        );
        
        try {
            // Prepare data for API call
            const submitData = {
                poLineId: lineId,
                tags: JSON.stringify(finalSelections)
            };
            
            // Call API to save PO line tags
            await savePOLineTag(submitData);
            
            // Update submitted tags
            setSubmittedLineTags(prev => ({
                ...prev,
                [lineId]: finalSelections
            }));
            
            // Clear pending selections and deselections
            setLineTagSelections(prev => {
                const newState = { ...prev };
                delete newState[lineId];
                return newState;
            });
            
            setLineTagsToDeselect(prev => {
                const newState = { ...prev };
                delete newState[lineId];
                return newState;
            });
        } catch (error) {
            console.error("Error submitting line tags:", error);
            alert("Error submitting line tags. Please try again.");
        }
    };

    const lineHasActiveSavedReceived = (lineId) => {
        const submitted = submittedLineStatuses[lineId] || [];
        const toRemove = lineStatusTagsToDeselect[lineId] || [];
        return submitted.some(
            (t) =>
                String(t.label || '').trim().toLowerCase() === 'received' &&
                !toRemove.some((r) => String(r.label || '').trim().toLowerCase() === 'received')
        );
    };

    // Line Status handler functions (only one dropdown open at a time)
    const toggleLineStatusDropdown = (lineId, event) => {
        if (lineStatusDropdownOpenId === lineId) {
            setLineStatusDropdownOpenId(null);
            setLineStatusDropdownPosition(null);
            return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        const parentRect = event.currentTarget.closest('.line-status-dropdown-container')?.getBoundingClientRect() || rect;
        setLineStatusDropdownOpenId(lineId);
        setLineStatusDropdownPosition({
            top: rect.bottom - parentRect.top,
            left: rect.left - parentRect.left,
            width: rect.width
        });
    };

    const handleLineStatusToggle = (lineId, tag) => {
        setLineStatusSelections(prev => {
            const currentSelections = prev[lineId] || [];
            const isSelected = currentSelections.some(selection => selection.label === tag.label);
            
            if (isSelected) {
                return {
                    ...prev,
                    [lineId]: currentSelections.filter(selection => selection.label !== tag.label)
                };
            } else {
                return {
                    ...prev,
                    [lineId]: [...currentSelections, tag]
                };
            }
        });
    };

    const handleLineStatusDeselect = (lineId, tag) => {
        setLineStatusTagsToDeselect(prev => {
            const currentDeselections = prev[lineId] || [];
            const isMarked = currentDeselections.some(t => t.label === tag.label);
            
            if (isMarked) {
                return {
                    ...prev,
                    [lineId]: currentDeselections.filter(t => t.label !== tag.label)
                };
            } else {
                return {
                    ...prev,
                    [lineId]: [...currentDeselections, tag]
                };
            }
        });
    };

    const hasLineStatusChangesToSubmit = (lineId) => {
        const currentSelections = lineStatusSelections[lineId] || [];
        const submittedSelections = submittedLineStatuses[lineId] || [];
        const tagsToRemove = lineStatusTagsToDeselect[lineId] || [];

        return currentSelections.length > 0 || tagsToRemove.length > 0;
    };

    // Announce the open line-status dropdown as our focused cell and hold a soft
    // lock on it - covers every open/close path through lineStatusDropdownOpenId.
    useEffect(() => {
        if (lineStatusDropdownOpenId == null) return undefined;
        const key = poLineCell(lineStatusDropdownOpenId);
        presenceFocus(key);
        return () => presenceBlur(key);
    }, [lineStatusDropdownOpenId, poLineCell, presenceFocus, presenceBlur]);

    /** Push a committed PO line status into the grid state. Shared by the local
     *  submit path and by remote saves arriving over presence. */
    const applyPoLineStatusPatch = useCallback((lineId, finalSelections) => {
        setSubmittedLineStatuses(prev => ({ ...prev, [lineId]: finalSelections }));

        setLineStatusSelections(prev => {
            const newState = { ...prev };
            delete newState[lineId];
            return newState;
        });

        setLineStatusTagsToDeselect(prev => {
            const newState = { ...prev };
            delete newState[lineId];
            return newState;
        });
    }, []);

    /** Someone else saved on this page - patch that one cell, no reload. */
    const applyRemoteSave = useCallback((evt) => {
        if (!evt || !evt.lineId) return;
        if (evt.kind === 'poLineStatus') {
            applyPoLineStatusPatch(evt.lineId, evt.value || []);
            return;
        }
        if (evt.kind === 'comment' && evt.poLineId && !evt.quoteId && evt.value) {
            const commentToStore = evt.value;
            setCommentsMap((prev) => {
                const current = prev[evt.lineId] || [];
                const filtered = current.filter((c) => String(c?.id) !== String(commentToStore.id));
                return { ...prev, [evt.lineId]: [...filtered, commentToStore] };
            });
        }
    }, [applyPoLineStatusPatch]);

    const handleLineStatusSubmit = async (lineId) => {
        const lockedBy = poLineLockOwner(lineId);
        if (lockedBy) {
            alert(`${lockedBy.name} is editing this line status. Try again in a moment.`);
            return;
        }

        const currentSelections = lineStatusSelections[lineId] || [];
        const submittedSelections = submittedLineStatuses[lineId] || [];
        const tagsToRemove = lineStatusTagsToDeselect[lineId] || [];
        
        // Calculate final selections
        const finalSelections = [
            ...currentSelections,
            ...submittedSelections.filter(
                tag => !tagsToRemove.some(removeTag => removeTag.label === tag.label)
            )
        ].filter((tag, index, array) =>
            array.findIndex(t => t.label === tag.label) === index
        );
        
        try {
            // Prepare data for API call
            const submitData = {
                poLineId: lineId,
                status: JSON.stringify(finalSelections)
            };
            
            // Call API to save PO line status
            await savePOLineStatus(submitData);

            applyPoLineStatusPatch(lineId, finalSelections);

            // Let everyone else on this page patch the same cell without a refresh.
            presence.broadcastSave({
                cell: poLineCell(lineId),
                kind: 'poLineStatus',
                lineId: String(lineId),
                value: finalSelections,
            });
        } catch (error) {
            console.error("Error submitting line status:", error);
            alert("Error submitting line status. Please try again.");
        }
    };

    // Close Line Tag dropdown on click outside (single open: close when click outside its container)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (lineTagDropdownOpenId != null) {
                const container = document.querySelector(`[data-line-tag-line-id="${CSS.escape(String(lineTagDropdownOpenId))}"]`);
                if (container && !container.contains(event.target)) {
                    setLineTagDropdownOpenId(null);
                    setLineTagDropdownPosition(null);
                }
            }
            if (lineStatusDropdownOpenId != null) {
                const container = document.querySelector(`[data-line-status-line-id="${CSS.escape(String(lineStatusDropdownOpenId))}"]`);
                if (container && !container.contains(event.target)) {
                    setLineStatusDropdownOpenId(null);
                    setLineStatusDropdownPosition(null);
                }
            }
            if (partLinkMenuLineId != null) {
                const container = document.querySelector(`[data-part-link-menu-line-id="${CSS.escape(String(partLinkMenuLineId))}"]`);
                if (container && !container.contains(event.target)) {
                    setPartLinkMenuLineId(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [lineTagDropdownOpenId, lineStatusDropdownOpenId, partLinkMenuLineId]);

    const renderPartNumberCell = (line) => {
        const partText = line.prcpart.substring(3) || 'N/A';
        const isOpen = partLinkMenuLineId === line.id;
        // partId: try direct field first, then nested part object (same pattern as InlineOrderDropdown)
        const partId = line.partId ?? line.part?.id ?? null;

        const partLinkMenuItems = [
            {
                key: 'inventory',
                label: 'Inventory',
                iconClass: 'fa fa-cubes',
                color: '#2d895f',
                href: partId ? `https://gigaflightinc.cetecerp.com/react/part/${partId}/inventory?newversion=1#` : null,
            },
            {
                key: 'waterfall',
                label: 'Waterfall',
                iconClass: 'fa fa-line-chart',
                color: '#f7ac21',
                href: partId ? `https://gigaflightinc.cetecerp.com/react/part/${partId}/waterfall` : null,
            },
            {
                key: 'purchase',
                label: 'Purchase',
                iconClass: 'fa fa-shopping-cart',
                color: '#4ab2d5',
                href: partId ? `https://gigaflightinc.cetecerp.com/react/part/${partId}/pos` : null,
            },
            {
                key: 'sales',
                label: 'Sales',
                iconClass: 'fa fa-bar-chart',
                color: '#e73030',
                href: partId ? `https://gigaflightinc.cetecerp.com/react/part/${partId}/orders` : null,
            },
        ];

        return (
            <td className="text-center" style={{ verticalAlign: 'middle' }}>
                <div
                    className="d-inline-flex align-items-center justify-content-center gap-1 position-relative"
                    style={{ maxWidth: '100%' }}
                    data-part-link-menu-line-id={line.id}
                >
                    <span style={{ minWidth: 0 }}>{partText}</span>
                    <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-haspopup="listbox"
                        title="Part links"
                        onClick={(e) => {
                            e.stopPropagation();
                            setPartLinkMenuLineId(isOpen ? null : line.id);
                        }}
                        style={{
                            flexShrink: 0,
                            width: 20,
                            height: 20,
                            padding: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fff',
                            border: '1px solid #0d6efd',
                            borderRadius: 3,
                            cursor: 'pointer',
                        }}
                    >
                        <i className="fa fa-link" style={{ fontSize: 10, color: '#0d6efd' }} aria-hidden="true" />
                    </button>
                    {isOpen && (
                        <ul
                            role="listbox"
                            className="m-0 p-0"
                            style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                marginTop: 4,
                                minWidth: 188,
                                listStyle: 'none',
                                background: '#fff',
                                borderRadius: 8,
                                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                                zIndex: 1050,
                                textAlign: 'left',
                                paddingTop: 4,
                                paddingBottom: 4,
                            }}
                        >
                            {partLinkMenuItems.map((item) => (
                                <li key={item.key} role="none">
                                    {item.href ? (
                                        <a
                                            href={item.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="d-flex align-items-center gap-2 text-decoration-none"
                                            style={{
                                                fontSize: 14,
                                                color: '#212529',
                                                padding: '8px 14px',
                                                whiteSpace: 'nowrap',
                                                display: 'flex',
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e7f1ff'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                            onClick={() => setPartLinkMenuLineId(null)}
                                        >
                                            <i
                                                className={item.iconClass}
                                                style={{ color: item.color, width: 20, textAlign: 'center' }}
                                                aria-hidden="true"
                                            />
                                            <span>{item.label}</span>
                                        </a>
                                    ) : (
                                        <span
                                            className="d-flex align-items-center gap-2"
                                            style={{
                                                fontSize: 14,
                                                color: '#adb5bd',
                                                padding: '8px 14px',
                                                whiteSpace: 'nowrap',
                                                cursor: 'not-allowed',
                                            }}
                                            title="Part ID not available"
                                        >
                                            <i
                                                className={item.iconClass}
                                                style={{ color: item.color, width: 20, textAlign: 'center', opacity: 0.4 }}
                                                aria-hidden="true"
                                            />
                                            <span>{item.label}</span>
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </td>
        );
    };

    // Helper function to render Line Tag UI
    const renderLineTagUI = (lineId) => (
        <div className="line-tag-dropdown-container position-relative" style={{ width: '100%' }} data-line-tag-line-id={lineId}>
            <div className="d-flex align-items-center gap-2" style={{ width: '100%' }}>
                <div
                    className="form-control d-flex align-items-start justify-content-between"
                    style={{
                        cursor: "pointer",
                        minHeight: "38px",
                        backgroundColor: "white",
                        border: "1px solid #ced4da",
                        width: "100%",
                        maxWidth: "100%",
                        overflow: "hidden",
                        padding: "4px 8px"
                    }}
                    onClick={(e) => toggleLineTagDropdown(lineId, e)}
                >
                    <div className="d-flex flex-wrap gap-1" style={{ width: "100%", alignContent: "flex-start" }}>
                        {/* Current selections */}
                        {(lineTagSelections[lineId] || []).map((tag, index) => (
                            <span
                                key={`current-${index}`}
                                className="badge"
                                style={{
                                    backgroundColor: tag.color,
                                    color: "white",
                                    fontSize: "0.75rem",
                                    padding: "4px 8px",
                                    borderRadius: "12px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px"
                                }}
                            >
                                {tag.label}
                            </span>
                        ))}

                        {/* Submitted tags with click to deselect */}
                        {(submittedLineTags[lineId] || []).map((tag, index) => {
                            const isMarkedForDeselection = (lineTagsToDeselect[lineId] || []).some(t => t.label === tag.label);
                            return (
                                <span
                                    key={`submitted-${index}`}
                                    className="badge"
                                    style={{
                                        backgroundColor: isMarkedForDeselection ? "#6c757d" : tag.color,
                                        color: "white",
                                        fontSize: "0.75rem",
                                        padding: "4px 8px",
                                        borderRadius: "12px",
                                        cursor: "pointer",
                                        opacity: isMarkedForDeselection ? 0.6 : 1,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "4px"
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLineTagDeselect(lineId, tag);
                                    }}
                                    title={isMarkedForDeselection ? "Click to keep this tag" : "Click to deselect this tag"}
                                >
                                    {tag.label}
                                </span>
                            );
                        })}

                        {(lineTagSelections[lineId] || []).length === 0 && (submittedLineTags[lineId] || []).length === 0 && (
                            <span className="text-muted">Select tags...</span>
                        )}
                    </div>
                </div>
                {hasLineTagChangesToSubmit(lineId) && (
                    <button
                        type="button"
                        className="btn btn-success btn-sm"
                        style={{
                            minWidth: "20px",
                            height: "20px",
                            padding: "0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "10px",
                            borderRadius: "2px"
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleLineTagSubmit(lineId);
                        }}
                        title="Submit changes"
                    >
                        ✓
                    </button>
                )}
            </div>

            {/* Dropdown menu - only one open at a time */}
            {lineTagDropdownOpenId === lineId && lineTagDropdownPosition && (
                <div
                    className="position-absolute"
                    style={{
                        top: `${lineTagDropdownPosition.top}px`,
                        left: `${lineTagDropdownPosition.left}px`,
                        width: `${lineTagDropdownPosition.width}px`,
                        zIndex: 1000,
                        backgroundColor: 'white',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        maxHeight: '200px',
                        overflowY: 'auto'
                    }}
                >
                    {LINE_TAG_TAGS.map((tag, tagIndex) => {
                        const isSelected = (lineTagSelections[lineId] || []).some(
                            selection => selection.label === tag.label
                        ) || (submittedLineTags[lineId] || []).some(
                            selection => selection.label === tag.label
                        );
                        return (
                            <div
                                key={tagIndex}
                                className="p-2 d-flex align-items-center"
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#f8f9fa' : 'white',
                                    borderBottom: '1px solid #f1f1f1'
                                }}
                                onClick={() => handleLineTagToggle(lineId, tag)}
                            >
                                <div
                                    className="me-2"
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '2px',
                                        backgroundColor: tag.color
                                    }}
                                ></div>
                                <span style={{ fontSize: '0.875rem' }}>
                                    {tag.label}
                                </span>
                                {isSelected && (
                                    <span className="ms-auto">✓</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    const handleKeyDown = (event) => {
        // debugger;
        // if (event.key === 'Enter' || event.key === 'Backspace') {
        fetchProduction(0, 10);
        // }
    };

    const getRowStyle = (date) => {
        // Row coloring removed - filter functionality is handled by API via daysFilter
        return {}; // Return empty object to maintain compatibility
    };

    const getDueDateStyle = (dueDate) => {

        const today = new Date();

        const targetDate = new Date(dueDate);

        const timeDiff = targetDate - today;

        const diffDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));





        // Past due: Light red
        if (diffDays < 0) {
            return {
                backgroundColor: '#ffcccc', // Light red
                color: 'black',
                textAlign: 'center'
            };
        }
        // Day of dock date: Pale blue
        else if (diffDays === 0) {
            return {
                backgroundColor: '#cce5ff', // Pale blue
                color: 'black',
                textAlign: 'center'
            };
        }
        // 2 days to dock date: Pale yellow
        else if (diffDays === 2) {
            return {
                backgroundColor: '#fff9cc', // Pale yellow
                color: 'black',
                textAlign: 'center'
            };
        }
        // Default: transparent
        return {
            backgroundColor: 'transparent',
            color: 'black',
            textAlign: 'center'
        };

    };

    const fetchData = async () => {
        debugger
        if (!useEffectprcPart || useEffectprcPart === '' || useEffectprcPart.length === 0) {
            setPrcPart([]);
            setPrcPartValue(useEffectprcPart);
            return;
        }
        const prcData = await POPartsData(useEffectprcPart, statusFilter, sortConfig.key, sortConfig.direction);
        debugger
        setPrcPart(prcData.production);
        setPrcPartValue(useEffectprcPart);
    };

    const fetchPOData = async () => {
        debugger
        if (!useEffectPO || useEffectPO === '' || useEffectPO.length === 0) {
            setPO([]);
            setPOValue(useEffectPO);
            return;
        }
        const prcData = await SearchPOData(useEffectPO, statusFilter, sortConfig.key, sortConfig.direction);
        debugger
        if (prcData != null) {
            setPO(prcData.content);
            setPOValue(useEffectPO);
        }

    };

    const fetchVendorData = async () => {
        debugger
        if (!useEffectVendor || useEffectVendor === '' || useEffectVendor.length === 0) {
            setVendor([]);
            setVendorValue(useEffectVendor);
            return;
        }
        const prcData = await SearchVendorData(useEffectVendor, statusFilter, sortConfig.key, sortConfig.direction);
        debugger
        if (prcData != null) {
            setVendor(prcData.content);
            setVendorValue(useEffectVendor);
        }
    };

    useEffect(() => {
        fetchData();

    }, [useEffectprcPart]);

    useEffect(() => {
        fetchPOData();

    }, [useEffectPO]);

    useEffect(() => {
        fetchVendorData();

    }, [useEffectVendor]);

    const [filterTabOpen, setFilterTab] = useState(false);

    return (
        <div className="box-body border b-white rounded-2">
            {/* 
            <h4 className="p-2 m-0 border-bottom">{messages.production_receiving_label}</h4> */}
            <div class="p-2 m-0 border-bottom row">
                <div class="col-12 d-flex justify-content-center align-items-center mb-3">
                    <div className="production-title">
                        <span className="production-text">{messages.production_receiving_label}</span>
                    </div>
                </div>
                <div class="col-12 d-flex justify-content-end align-items-center gap-3">
                    <PresenceBar
                        here={presence.here}
                        cells={presence.cells}
                        connected={presence.connected}
                        me={presence.me}
                    />
                    <Link to="/Highlights" className='btn btn-primary'>{messages.Highlights_label}</Link>
                    <button class="btn border-success d-flex align-items-center gap-2 px-3" onClick={() => { setFilterTab(filterTabOpen === true ? false : true) }} >
                        <img src={filterIcon} style={{ width: "16px", height: "16px" }} />
                        <span>Filter</span>
                    </button>
                    <button class="btn border-success d-flex align-items-center gap-2 px-3" onClick={() => { setExport(true); setExportClicked(exportsClicked ? false : true) }}>
                        <img src={excelIcon} style={{ width: "16px", height: "16px" }} />
                        <span>Export</span>
                    </button>
                </div>
            </div>
            {filterTabOpen ?
                <div className="row p-2 m-0" style={{ background: '#d3d3d333' }}>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Assigned / Owner</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={filterAssignedOwner} onInput={(e) => setFilterAssignedOwner(e.target.value)} placeholder="" />
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>P.O#</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={POValue} onInput={(e) => setUseEffectPO(e.target.value)} />
                        {PO.length > 0 && (
                            <ul
                                style={{
                                    listStyleType: 'none',
                                    margin: '10px 0',
                                    padding: '0',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    position: 'absolute',
                                    background: 'white',
                                    top: '155px',
                                    zIndex: '10'
                                }}
                            >
                                {PO.map((item, index) => (
                                    <li
                                        key={index}
                                        style={{
                                            padding: '10px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #eee',
                                        }}
                                        onClick={() => {
                                            setPOValue(item.ponum);
                                            setPO([]);
                                        }}
                                    >
                                        {item.ponum}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Line#</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={filterLineNo} onInput={(e) => setFilterLineNo(e.target.value)} />
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Vendor</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={VendorValue} onInput={(e) => setUseEffectVendor(e.target.value)} />
                        {Vendor.length > 0 && (
                            <ul
                                style={{
                                    listStyleType: 'none',
                                    margin: '10px 0',
                                    padding: '0',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    position: 'absolute',
                                    background: 'white',
                                    top: '155px',
                                    zIndex: '10'
                                }}
                            >
                                {Vendor.map((item, index) => (
                                    <li
                                        key={index}
                                        style={{
                                            padding: '10px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #eee',
                                        }}
                                        onClick={() => {
                                            setVendorValue(item.name);
                                            setVendor([]);
                                        }}
                                    >
                                        {item.name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Part#</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={prcPartValue} onInput={(e) => setUseEffectPrcPart(e.target.value)} />
                        {prcPart.length > 0 && (
                            <ul
                                style={{
                                    listStyleType: 'none',
                                    margin: '10px 0',
                                    padding: '0',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    position: 'absolute',
                                    background: 'white',
                                    top: '200px',
                                    zIndex: '10'
                                }}
                            >
                                {prcPart.map((item, index) => (
                                    <li
                                        key={index}
                                        style={{
                                            padding: '10px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #eee',
                                        }}
                                        onClick={() => {
                                            setPrcPartValue(item.prcpart);
                                            setPrcPart([]);
                                        }}
                                    >
                                        {item.prcpart.substring(3)}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Qty</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={filterQty} onInput={(e) => setFilterQty(e.target.value)} />
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Vendor Part#</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={filterVendorPart} onInput={(e) => setFilterVendorPart(e.target.value)} />
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Dock Date</div>
                        <div className='d-flex align-items-center gap-1' style={{ width: '65%', minWidth: 0 }}>
                            <DatePicker
                                dateFormat="yyyy-MM-dd"
                                className="form-control form-control-sm"
                                id="date-start"
                                placeholderText='Start'
                                selected={startDate}
                                onChange={(e) => setStartDate(e ? e.toISOString().split('T')[0] : '')}
                            />
                            <span className="text-nowrap">-</span>
                            <DatePicker
                                dateFormat="yyyy-MM-dd"
                                className="form-control form-control-sm date-picker-no-cursor"
                                id="date-end"
                                selected={endDate}
                                placeholderText='End'
                                onChange={(e) => setEndDate(e ? e.toISOString().split('T')[0] : '')}
                            />
                        </div>
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Line Tag</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={filterLineTag} onInput={(e) => setFilterLineTag(e.target.value)} />
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>PO Comment</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={filterPOComment} onInput={(e) => setFilterPOComment(e.target.value)} />
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Notes</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={filterNotes} onInput={(e) => setFilterNotes(e.target.value)} />
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Line Status</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={filterLineStatus} onInput={(e) => setFilterLineStatus(e.target.value)} />
                    </div>
                    <div className="col-12 d-flex justify-content-end align-items-center pt-1 pb-2">
                        <input type="button" class="btn btn-primary py-2 px-4" value='Submit' onClick={() => fetchProduction((currentPage - 1) * itemsPerPage, itemsPerPage, statusFilter)} style={{ minWidth: '180px' }} />
                    </div>
                </div> : ''}
            <div className='row p-2'>
                <div className="col-1 d-flex justify-content-start align-items-bottom">
                    <select
                        className="px-3 py-1 border rounded-1 d-inline"
                        value={itemsPerPage >= 999999 ? 999999 : itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="999999">All</option>
                    </select>
                </div>
                <div className="col-8">
                    {/* Toggle buttons for Open, Closed, and All status */}
                    <div className="btn-group d-none" role="group" aria-label="Order Status Toggle" >
                        <button
                            type="button"
                            className={`btn ${statusFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => { setStatusFilter('all'); setDaysFilter(0) }}
                        >
                            All
                        </button>
                        <button
                            type="button"
                            className={`btn ${statusFilter === 'Open' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setStatusFilter('Open')}
                        >
                            Open
                        </button>
                        <button
                            type="button"
                            className={`btn ${statusFilter === 'Closed' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setStatusFilter('Closed')}
                        >
                            Closed
                        </button>

                    </div>
                    <div className='d-flex justify-content-start align-items-center ps-5' style={{ gap: '15px' }}>
                        {/* 1 Day Filter - toggle: click again to show all */}
                        <button
                            type="button"
                            className={`btn ${daysFilter === 1 ? 'btn-danger' : 'btn-outline-danger'} date-filter-btn ${daysFilter === 1 ? 'active' : ''}`}
                            onClick={() => setDaysFilter((prev) => (prev === 1 ? 0 : 1))}
                        >
                            🔥 1 Day
                        </button>
                        
                        {/* Less than a week Filter - toggle */}
                        <button
                            type="button"
                            className={`btn ${daysFilter === 2 ? 'btn-danger' : 'btn-outline-danger'} date-filter-btn ${daysFilter === 2 ? 'active' : ''}`}
                            onClick={() => setDaysFilter((prev) => (prev === 2 ? 0 : 2))}
                        >
                            🔥 less than a week
                        </button>
                        
                        {/* 1-3 weeks Filter - toggle */}
                        <button
                            type="button"
                            className={`btn date-filter-btn one-to-three-weeks ${daysFilter === 3 ? 'active' : ''}`}
                            onClick={() => setDaysFilter((prev) => (prev === 3 ? 0 : 3))}
                        >
                            ⏰ 1-3 weeks
                        </button>
                        
                        {/* 4 weeks Filter - toggle */}
                        <button
                            type="button"
                            className={`btn date-filter-btn four-weeks ${daysFilter === 4 ? 'active' : ''}`}
                            onClick={() => setDaysFilter((prev) => (prev === 4 ? 0 : 4))}
                        >
                            📆 4 weeks
                        </button>
                    </div>
                </div>

                <div className="col-3 d-flex justify-content-end align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setShowColumnModal(true)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '0.875rem',
                            padding: '6px 12px'
                        }}
                        title="Show/Hide Columns"
                    >
                        <i className="fa fa-columns" aria-hidden="true"></i>
                        Columns
                    </button>
                    <input
                        type="text"
                        className="form-control"
                        id="search"
                        placeholder={messages.search_label}
                        value={searchTerm}
                        onInput={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                        style={{ maxWidth: '200px' }}
                    />
                </div>
            </div>
            {/* New Purchase Orders Section */}
            {newPurchaseOrdersData && newPurchaseOrdersData.filter(line => !archivedPOLineIds.has(line.id)).length > 0 && (
                <div className="row mt-2" style={{ width: '98.8%', margin: '0 8px' }}>
                    <div className="col-12 mb-2">
                        <div
                            className="table-section-title"
                            onClick={() => setNewPurchaseOrdersExpanded((prev) => !prev)}
                            style={{ cursor: 'pointer' }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setNewPurchaseOrdersExpanded((prev) => !prev); } }}
                            aria-expanded={newPurchaseOrdersExpanded}
                        >
                            <span className="table-section-text">New Purchase Orders</span>
                            <span className="ms-2" style={{ fontSize: '1.5em', display: 'inline-block', minWidth: '28px', lineHeight: 1 }} aria-hidden="true">
                                {newPurchaseOrdersExpanded ? '\u25BC' : '\u25B6'}
                            </span>
                        </div>
                    </div>
                    {newPurchaseOrdersExpanded && (
                    <>
                    <div className="col-12 p-0">
                        <div
                            className="overflow-auto bridge-manual-scroll"
                            style={{
                                width: '100%',
                                margin: 0,
                                overflowX: 'auto',
                                overflowY: 'auto',
                                maxHeight: '95vh',
                            }}
                        >
                            <table className="table gf-table table-bordered table-hover mb-0 production-table compact-data-table" style={{ tableLayout: 'auto', width: 'auto', minWidth: '100%' }}>
                                    <thead className="sticky-top bg-light" style={{ top: '0', zIndex: 10 }}>
                                        <tr>
                                            {isColumnVisible('assigned_owner') && <th className="text-center" style={{ width: 'auto', minWidth: '50px' }}>Assigned / Owner</th>}
                                            {isColumnVisible('po_label') && <th className="text-center production-po-col" onClick={() => handleSort('ponum')}>
                                                {messages.po_label} {getSortIcon('ponum')}
                                            </th>}
                                            {isColumnVisible('line_no') && <th className="text-center" onClick={() => handleSort('linenum')}>
                                                Line# {getSortIcon('linenum')}
                                            </th>}
                                            {isColumnVisible('vendor_label') && <th className="text-center" onClick={() => handleSort('vendor_name')}>
                                                {messages.vendor_label} {getSortIcon('vendor_name')}
                                            </th>}
                                            {isColumnVisible('part') && <th className="text-center" onClick={() => handleSort('prcpart')}>
                                                Part# {getSortIcon('prcpart')}
                                            </th>}
                                            {isColumnVisible('qty') && <th className="text-center">QTY</th>}
                                            {isColumnVisible('vendor_part') && <th className="text-center">Vendor Part#</th>}
                                            {isColumnVisible('dock_date') && <th className="text-center" onClick={() => handleSort('duedate')} style={{ minWidth: '160px', width: '160px' }}>
                                                Dock Date {getSortIcon('duedate')}
                                            </th>}
                                            {isColumnVisible('line_tag') && <th className="text-center" style={{ minWidth: '150px', maxWidth: '200px' }}>Line Tag</th>}
                                            {isColumnVisible('comments_po') && <th className="text-center" style={{ minWidth: '200px', maxWidth: '300px', width: '250px' }}>{messages.comments_po_label}</th>}
                                            {isColumnVisible('comments') && <th className="text-center">{messages.comments_label}</th>}
                                            {isColumnVisible('line_status') && <th className="text-center" style={{ minWidth: '200px', width: '200px', maxWidth: '300px' }}>Line Status</th>}
                                            {isColumnVisible('docs') && <th className="text-center production-doc-col">Docs</th>}
                                            {isColumnVisible('archive') && <th className="text-center" style={{ width: 'auto', minWidth: '50px' }}>Archive</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredNewOrdersData.map((line, lineIndex) => {
                                            const dueDate = new Date(line.duedate);
                                            const today = new Date();
                                            const timeDiff = dueDate - today;
                                            const diffDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

                                            return (
                                                <tr 
                                                    key={`new-po-${line.po.id}-${lineIndex}`} 
                                                    className={lineHasActiveSavedReceived(line.id) ? 'po-line-row-received' : undefined}
                                                    {...getRowStyle(line.duedate)}
                                                >
                                                    {isColumnVisible('assigned_owner') && <td className="text-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0"
                                                            title="Assign user to this purchase order"
                                                            style={{
                                                                fontSize: '1.2rem',
                                                                color: '#6c757d',
                                                                textDecoration: 'none',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <i className="fa fa-plus" aria-hidden="true"></i>
                                                        </button>
                                                    </td>}
                                                    {isColumnVisible('po_label') && <td className="text-center production-po-col">
                                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                                            <a
                                                                href={`https://gigaflightinc.cetecerp.com/purchaseorder/${line.po.id}/view`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ textDecoration: 'none' }}
                                                            >
                                                                {line.po.ponum}
                                                            </a>
                                                            {(() => {
                                                                const folderLink = line.po?.folderUrl?.trim() || null;
                                                                const folderTitle = folderLink ? `Open folder: ${folderLink}` : 'No folder link saved yet';
                                                                return (
                                                                    <a
                                                                        href={folderLink || '#'}
                                                                        onClick={(e) => { if (!folderLink) e.preventDefault(); }}
                                                                        target={folderLink ? '_blank' : undefined}
                                                                        rel={folderLink ? 'noopener noreferrer' : undefined}
                                                                        style={{ cursor: folderLink ? 'pointer' : 'not-allowed', textDecoration: 'none' }}
                                                                        title={folderTitle}
                                                                    >
                                                                        <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: folderLink ? '#ff8c00' : '#cccccc' }}></i>
                                                                    </a>
                                                                );
                                                            })()}
                                                        </div>
                                                    </td>}
                                                    {isColumnVisible('line_no') && <td>{line.linenum}</td>}
                                                    {isColumnVisible('vendor_label') && <td className="vendor-cell">
                                                        {(line.po.vendor_id ?? line.po.vendorId) != null ? (
                                                            <a
                                                                href={`https://gigaflightinc.cetecerp.com/vendor/${line.po.vendor_id ?? line.po.vendorId}/view`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ textDecoration: 'none' }}
                                                            >
                                                                {line.po.vendorName}
                                                            </a>
                                                        ) : (
                                                            <span>{line.po.vendorName}</span>
                                                        )}
                                                    </td>}
                                                    {isColumnVisible('part') && renderPartNumberCell(line)}
                                                    {isColumnVisible('qty') && <td className="text-center">{line.orderqty ? parseFloat(line.orderqty) : 'N/A'}</td>}
                                                    {isColumnVisible('vendor_part') && <td className="vendor-part-cell">{line.cross_part ? line.cross_part : ''}</td>}
                                                    {isColumnVisible('dock_date') && <td style={{...getDueDateStyle(line.duedate), whiteSpace: 'nowrap', minWidth: '160px', width: '160px', textAlign: 'center' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                                            <span>{line.duedate}</span>
                                                            <span
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '32px',
                                                                    height: '32px',
                                                                    borderRadius: '50%',
                                                                    border: diffDays < 0 ? '1.5px solid #dc3545' : '1.5px solid #6c757d',
                                                                    backgroundColor: diffDays < 0 ? '#ffcccc' : 'transparent',
                                                                    color: diffDays < 0 ? '#dc3545' : '#000',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '11px',
                                                                    flexShrink: 0
                                                                }}
                                                            >
                                                                {diffDays}
                                                            </span>
                                                        </div>
                                                    </td>}
                                                    {isColumnVisible('line_tag') && <td className="text-center" style={{ minWidth: '150px', maxWidth: '200px' }}>
                                                        {renderLineTagUI(line.id)}
                                                    </td>}
                                                    {isColumnVisible('comments_po') && <td className="shipping-comments-cell">
                                                        <div className="shipping-comments-wrapper">
                                                            <div 
                                                                className={`shipping-comments-content ${expandedPOComments.has(line.id) ? 'expanded' : ''}`}
                                                            >
                                                                {line.po.comment ? line.po.comment : ''}
                                                            </div>
                                                            {line.po.comment && isCommentLong(line.po.comment) && (
                                                                <button
                                                                    type="button"
                                                                    className="comment-expand-button"
                                                                    onClick={() => togglePOComment(line.id)}
                                                                    title={expandedPOComments.has(line.id) ? "Collapse" : "Expand"}
                                                                >
                                                                    <svg 
                                                                        className={`comment-expand-arrow ${expandedPOComments.has(line.id) ? 'expanded' : ''}`}
                                                                        xmlns="http://www.w3.org/2000/svg" 
                                                                        width="12" 
                                                                        height="12" 
                                                                        viewBox="0 0 16 16" 
                                                                        fill="currentColor"
                                                                    >
                                                                        <path d="M8 11L3 6h10l-5 5z"/>
                                                                    </svg>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>}
                                                    {isColumnVisible('comments') && <td>
                                                        {line.comment ? (
                                                            <div className="d-flex align-items-start gap-0">
                                                                <textarea style={{ width: '180px' }}
                                                                    value={
                                                                        line.comment && line.comment.length > 0 && line.comment[line.comment.length - 1].comment
                                                                            ? line.comment[line.comment.length - 1].comment
                                                                            : ''
                                                                    }
                                                                    className="form-control"
                                                                    rows={1}
                                                                />
                                                                {renderNotesPresence(line.id)}
                                                                <Hover
                                                                    poLineId={line.id}
                                                                    comments={[...(line.comment || []), ...(commentsMap[line.id] || [])]}
                                                                    orderNumber={line.po?.ponum}
                                                                    customerName={line.po?.vendorName}
                                                                    lineInfo={line.linenum != null ? `Line ${line.linenum}` : undefined}
                                                                    sourcePage="purchase"
                                                                    onVisibleChange={(isVisible) => handleNotesVisibility(line.id, isVisible)}
                                                                    onAddComment={(newComment, status) => handleAddComment(line.id, newComment, status)}
                                                                    onDeleteComment={(commentId) => handleDeleteComment(line.id, commentId)}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-light"
                                                                        style={{ padding: "6px", borderRadius: "50%", border: "1px solid #ccc" }}
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 0 512 512">
                                                                            <path
                                                                                fill="currentColor"
                                                                                d="M344 0L488 0c13.3 0 24 10.7 24 24l0 144c0 9.7-5.8 18.5-14.8 22.2s-19.3 1.7-26.2-5.2l-39-39-87 87c-9.4 9.4-24.6 9.4-33.9 0l-32-32c-9.4-9.4-9.4-24.6 0-33.9l87-87L327 41c-6.9-6.9-8.9-17.2-5.2-26.2S334.3 0 344 0zM168 512L24 512c-13.3 0-24-10.7-24-24L0 344c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2l39 39 87-87c9.4-9.4 24.6-9.4 33.9 0l32 32c9.4 9.4 9.4 24.6 0 33.9l-87 87 39 39c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8z"
                                                                            />
                                                                        </svg>
                                                                    </button>
                                                                </Hover>
                                                            </div>
                                                        ) : (
                                                            <textarea className="form-control" rows={1} />
                                                        )}
                                                    </td>}
                                                    {isColumnVisible('line_status') && <td className="text-center" style={{ minWidth: '200px', width: '200px', maxWidth: '300px' }}>
                                                        <div className="line-status-dropdown-container position-relative" style={{ width: '100%' }} data-line-status-line-id={line.id}>
                                                            <div className="d-flex align-items-center gap-2" style={{ width: '100%' }}>
                                                                <div
                                                                    className="form-control d-flex align-items-start justify-content-between"
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        minHeight: "38px",
                                                                        backgroundColor: "white",
                                                                        border: "1px solid #ced4da",
                                                                        width: "100%",
                                                                        maxWidth: "100%",
                                                                        overflow: "visible",
                                                                        padding: "4px 8px"
                                                                    }}
                                                                    onClick={(e) => toggleLineStatusDropdown(line.id, e)}
                                                                >
                                                                    <div className="d-flex flex-wrap gap-1" style={{ width: "100%", alignContent: "flex-start" }}>
                                                                        {/* Current selections */}
                                                                        {(lineStatusSelections[line.id] || []).map((tag, index) => (
                                                                            <span
                                                                                key={`current-status-${index}`}
                                                                                className="badge"
                                                                                style={{
                                                                                    backgroundColor: tag.color,
                                                                                    color: "white",
                                                                                    fontSize: "0.75rem",
                                                                                    padding: "4px 8px",
                                                                                    borderRadius: "12px",
                                                                                    display: "inline-flex",
                                                                                    alignItems: "center",
                                                                                    gap: "4px"
                                                                                }}
                                                                            >
                                                                                {tag.label}
                                                                            </span>
                                                                        ))}

                                                                        {/* Submitted tags with click to deselect */}
                                                                        {(submittedLineStatuses[line.id] || []).map((tag, index) => {
                                                                            const isMarkedForDeselection = (lineStatusTagsToDeselect[line.id] || []).some(t => t.label === tag.label);
                                                                            return (
                                                                                <span
                                                                                    key={`submitted-status-${index}`}
                                                                                    className="badge"
                                                                                    style={{
                                                                                        backgroundColor: isMarkedForDeselection ? "#6c757d" : tag.color,
                                                                                        color: "white",
                                                                                        fontSize: "0.75rem",
                                                                                        padding: "4px 8px",
                                                                                        borderRadius: "12px",
                                                                                        cursor: "pointer",
                                                                                        opacity: isMarkedForDeselection ? 0.6 : 1,
                                                                                        display: "inline-flex",
                                                                                        alignItems: "center",
                                                                                        gap: "4px"
                                                                                    }}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleLineStatusDeselect(line.id, tag);
                                                                                    }}
                                                                                    title={isMarkedForDeselection ? "Click to keep this tag" : "Click to deselect this tag"}
                                                                                >
                                                                                    {tag.label}
                                                                                </span>
                                                                            );
                                                                        })}

                                                                        {(lineStatusSelections[line.id] || []).length === 0 && (submittedLineStatuses[line.id] || []).length === 0 && (
                                                                            <span className="text-muted">Select tags...</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {renderPoLinePresence(line.id)}
                                                                {hasLineStatusChangesToSubmit(line.id) && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-success btn-sm"
                                                                        style={{
                                                                            minWidth: "20px",
                                                                            height: "20px",
                                                                            padding: "0",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            fontSize: "10px",
                                                                            borderRadius: "2px"
                                                                        }}
                                                                        disabled={Boolean(poLineLockOwner(line.id))}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleLineStatusSubmit(line.id);
                                                                        }}
                                                                        title={
                                                                            poLineLockOwner(line.id)
                                                                                ? `${poLineLockOwner(line.id).name} is editing this line status`
                                                                                : "Submit changes"
                                                                        }
                                                                    >
                                                                        ✓
                                                                    </button>
                                                                )}
                                                            </div>

                                                            {/* Dropdown menu - only one open at a time */}
                                                            {lineStatusDropdownOpenId === line.id && lineStatusDropdownPosition && (
                                                                <div
                                                                    className="position-absolute"
                                                                    style={{
                                                                        top: `${lineStatusDropdownPosition.top}px`,
                                                                        left: `${lineStatusDropdownPosition.left}px`,
                                                                        width: `${lineStatusDropdownPosition.width}px`,
                                                                        zIndex: 1000,
                                                                        backgroundColor: 'white',
                                                                        border: '1px solid #ced4da',
                                                                        borderRadius: '4px',
                                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                                        maxHeight: '200px',
                                                                        overflowY: 'auto'
                                                                    }}
                                                                >
                                                                    {LINE_STATUS_TAGS.map((tag, tagIndex) => {
                                                                        const isSelected = (lineStatusSelections[line.id] || []).some(
                                                                            selection => selection.label === tag.label
                                                                        ) || (submittedLineStatuses[line.id] || []).some(
                                                                            selection => selection.label === tag.label
                                                                        );
                                                                        return (
                                                                            <div
                                                                                key={tagIndex}
                                                                                className="p-2 d-flex align-items-center"
                                                                                style={{
                                                                                    cursor: 'pointer',
                                                                                    backgroundColor: isSelected ? '#f8f9fa' : 'white',
                                                                                    borderBottom: '1px solid #f1f1f1'
                                                                                }}
                                                                                onClick={() => handleLineStatusToggle(line.id, tag)}
                                                                            >
                                                                                <div
                                                                                    className="me-2"
                                                                                    style={{
                                                                                        width: '12px',
                                                                                        height: '12px',
                                                                                        borderRadius: '2px',
                                                                                        backgroundColor: tag.color
                                                                                    }}
                                                                                ></div>
                                                                                <span style={{ fontSize: '0.875rem' }}>
                                                                                    {tag.label}
                                                                                </span>
                                                                                {isSelected && (
                                                                                    <span className="ms-auto">✓</span>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>}
                                                    {isColumnVisible('docs') && <td className="text-center production-docs-col">
                                                        <div className="d-flex flex-column align-items-center justify-content-center gap-2" style={{ maxWidth: '220px' }}>
                                                            {/* Display existing URLs as chips */}
                                                            {(poAccessUrls[line.id] || []).length > 0 && (
                                                                <div className="d-flex flex-wrap gap-1 justify-content-center" style={{ width: '100%' }}>
                                                                    {(poAccessUrls[line.id] || []).map((urlItem) => (
                                                                        <div
                                                                            key={`po-url-${urlItem.id}`}
                                                                            style={{
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                backgroundColor: '#f0f0f0',
                                                                                border: '1px solid #ddd',
                                                                                borderRadius: '12px',
                                                                                padding: '3px 6px',
                                                                                fontSize: '10px',
                                                                                maxWidth: '180px',
                                                                                position: 'relative'
                                                                            }}
                                                                        >
                                                                            <a
                                                                                href={urlItem.url}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{
                                                                                    textDecoration: 'none',
                                                                                    color: '#007bff',
                                                                                    fontWeight: '500',
                                                                                    marginRight: '4px',
                                                                                    flexGrow: 1,
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap'
                                                                                }}
                                                                                title={`${urlItem.urlName}: ${urlItem.url}`}
                                                                            >
                                                                                {urlItem.urlName}
                                                                            </a>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleDeletePoAccessUrl(line.id, urlItem.id, line.po?.id)}
                                                                                style={{
                                                                                    background: 'none',
                                                                                    border: 'none',
                                                                                    color: '#dc3545',
                                                                                    cursor: 'pointer',
                                                                                    padding: '0',
                                                                                    marginLeft: '2px',
                                                                                    fontSize: '12px',
                                                                                    lineHeight: '1',
                                                                                    width: '16px',
                                                                                    height: '16px',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center'
                                                                                }}
                                                                                title="Delete this URL"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {/* Add button */}
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                style={{
                                                                    fontSize: '0.7rem',
                                                                    padding: '3px 8px',
                                                                    height: '28px'
                                                                }}
                                                                onClick={() => handleDocsClick(line)}
                                                                title="Add URL link"
                                                            >
                                                                <i className="fa fa-plus" style={{ marginRight: '3px' }}></i>
                                                                Add
                                                            </button>
                                                        </div>
                                                    </td>}
                                                    {isColumnVisible('archive') && <td className="text-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0"
                                                            onClick={() => handleArchiveOrder(line)}
                                                            title="Archive this PO line"
                                                            style={{
                                                                fontSize: '1.2rem',
                                                                color: '#6c757d',
                                                                textDecoration: 'none'
                                                            }}
                                                        >
                                                            <i className="fa fa-paper-plane" aria-hidden="true"></i>
                                                        </button>
                                                    </td>}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                        </div>
                    </div>
                </>
                )}
                </div>
            )}
            {/* All Purchase Orders Section */}
            <div className="row mt-2" style={{ width: '98.8%', margin: '0 8px' }}>
                <div className="col-12 mb-2">
                    <div className="table-section-title">
                        <span className="table-section-text">All Purchase Orders</span>
                    </div>
                </div>
                <div className="col-12 p-0">
                    <div
                        className="overflow-auto bridge-manual-scroll"
                        style={{
                            width: '100%',
                            margin: 0,
                            overflowX: 'auto',
                            overflowY: 'auto',
                            maxHeight: '95vh',
                        }}
                    >
                        {loading ? (
                            <div className="text-center vh-50 d-flex justify-content-center align-items-center">
                                <div className="spinner-border" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <table className="table gf-table table-bordered table-hover mb-0 production-table compact-data-table" style={{ tableLayout: 'auto', width: 'auto', minWidth: '100%' }}>
                                <thead className="sticky-top bg-light" style={{ top: '0', zIndex: 10 }}>
                                    <tr>
                                        {isColumnVisible('assigned_owner') && <th className="text-center" style={{ width: 'auto', minWidth: '50px' }}>Assigned / Owner</th>}
                                        {isColumnVisible('po_label') && <th className="text-center production-po-col" onClick={() => handleSort('ponum')}>
                                            {messages.po_label} {getSortIcon('ponum')}
                                        </th>}
                                        {isColumnVisible('line_no') && <th className="text-center" onClick={() => handleSort('linenum')}>
                                            Line# {getSortIcon('linenum')}
                                        </th>}
                                        {isColumnVisible('vendor_label') && <th className="text-center" onClick={() => handleSort('vendor_name')}>
                                            {messages.vendor_label} {getSortIcon('vendor_name')}
                                        </th>}
                                        {isColumnVisible('part') && <th className="text-center" onClick={() => handleSort('prcpart')}>
                                            Part# {getSortIcon('prcpart')}
                                        </th>}
                                        {isColumnVisible('qty') && <th className="text-center">QTY</th>}
                                        {isColumnVisible('vendor_part') && <th className="text-center">Vendor Part#</th>}
                                        {isColumnVisible('dock_date') && <th className="text-center" onClick={() => handleSort('duedate')} style={{ minWidth: '160px', width: '160px' }}>
                                            Dock Date {getSortIcon('duedate')}
                                        </th>}
                                        {isColumnVisible('line_tag') && <th className="text-center" style={{ minWidth: '150px', maxWidth: '200px' }}>Line Tag</th>}
                                        {isColumnVisible('comments_po') && <th className="text-center" style={{ minWidth: '200px', maxWidth: '300px', width: '250px' }}>{messages.comments_po_label}</th>}
                                        {isColumnVisible('comments') && <th className="text-center">{messages.comments_label}</th>}
                                        {isColumnVisible('line_status') && <th className="text-center" style={{ minWidth: '200px', width: '200px', maxWidth: '300px' }}>Line Status</th>}
                                        {isColumnVisible('docs') && <th className="text-center  production-docs-col">Docs</th>}
                                        {isColumnVisible('archive') && <th className="text-center" style={{ width: 'auto', minWidth: '50px' }}>Archive</th>}
                                        {/* <th className="text-center">{messages.action_label}</th> */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {productionData != undefined && productionData.length > 0 ? (
                                        filteredProductionData.map((line, lineIndex) => {
                                            const dueDate = new Date(line.duedate);
                                            const today = new Date();
                                            const timeDiff = dueDate - today;
                                            const diffDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

                                            return (
                                                <tr
                                                    key={`${line.po.id}-${lineIndex}`}
                                                    className={lineHasActiveSavedReceived(line.id) ? 'po-line-row-received' : undefined}
                                                    {...getRowStyle(line.duedate)}
                                                >
                                                    {isColumnVisible('assigned_owner') && <td className="text-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0"
                                                            title="Assign user to this purchase order"
                                                            style={{
                                                                fontSize: '1.2rem',
                                                                color: '#6c757d',
                                                                textDecoration: 'none',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <i className="fa fa-plus" aria-hidden="true"></i>
                                                        </button>
                                                    </td>}
                                                    {isColumnVisible('po_label') && <td className="text-center production-docs-col">
                                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                                            <a
                                                                href={`https://gigaflightinc.cetecerp.com/purchaseorder/${line.po.id}/view`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ textDecoration: 'none' }}
                                                            >
                                                                {line.po.ponum}
                                                            </a>
                                                            {(() => {
                                                                const folderLink = line.po?.folderUrl?.trim() || null;
                                                                const folderTitle = folderLink ? `Open folder: ${folderLink}` : 'No folder link saved yet';
                                                                return (
                                                                    <a
                                                                        href={folderLink || '#'}
                                                                        onClick={(e) => { if (!folderLink) e.preventDefault(); }}
                                                                        target={folderLink ? '_blank' : undefined}
                                                                        rel={folderLink ? 'noopener noreferrer' : undefined}
                                                                        style={{ cursor: folderLink ? 'pointer' : 'not-allowed', textDecoration: 'none' }}
                                                                        title={folderTitle}
                                                                    >
                                                                        <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: folderLink ? '#ff8c00' : '#cccccc' }}></i>
                                                                    </a>
                                                                );
                                                            })()}
                                                        </div>
                                                    </td>}
                                                    {isColumnVisible('line_no') && <td>{line.linenum}</td>}
                                                    {/* <td>{getVendorName(line.po.vennum)}</td> */}
                                                    {isColumnVisible('vendor_label') && <td className="vendor-cell">
                                                        {(line.po.vendor_id ?? line.po.vendorId) != null ? (
                                                            <a
                                                                href={`https://gigaflightinc.cetecerp.com/vendor/${line.po.vendor_id ?? line.po.vendorId}/view`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ textDecoration: 'none' }}
                                                            >
                                                                {line.po.vendorName}
                                                            </a>
                                                        ) : (
                                                            <span>{line.po.vendorName}</span>
                                                        )}
                                                    </td>}
                                                    {isColumnVisible('part') && renderPartNumberCell(line)}
                                                    {isColumnVisible('qty') && <td className="text-center">{line.orderqty ? parseFloat(line.orderqty) : 'N/A'}</td>}
                                                    {isColumnVisible('vendor_part') && <td className="vendor-part-cell">{line.cross_part?line.cross_part:''}</td>}
                                                    {isColumnVisible('dock_date') && <td style={{...getDueDateStyle(line.duedate), whiteSpace: 'nowrap', minWidth: '160px', width: '160px', textAlign: 'center' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                                            <span>{line.duedate}</span>
                                                            <span
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '32px',
                                                                    height: '32px',
                                                                    borderRadius: '50%',
                                                                    border: diffDays < 0 ? '1.5px solid #dc3545' : '1.5px solid #6c757d',
                                                                    backgroundColor: diffDays < 0 ? '#ffcccc' : 'transparent',
                                                                    color: diffDays < 0 ? '#dc3545' : '#000',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '11px',
                                                                    flexShrink: 0
                                                                }}
                                                            >
                                                                {diffDays}
                                                            </span>
                                                        </div>
                                                    </td>}
                                                    {isColumnVisible('line_tag') && <td className="text-center" style={{ minWidth: '150px', maxWidth: '200px' }}>
                                                        {renderLineTagUI(line.id)}
                                                    </td>}
                                                    {isColumnVisible('comments_po') && <td className="shipping-comments-cell">
                                                        <div className="shipping-comments-wrapper">
                                                            <div 
                                                                className={`shipping-comments-content ${expandedPOComments.has(line.id) ? 'expanded' : ''}`}
                                                            >
                                                                {line.po.comment ? line.po.comment : ''}
                                                            </div>
                                                            {line.po.comment && isCommentLong(line.po.comment) && (
                                                                <button
                                                                    type="button"
                                                                    className="comment-expand-button"
                                                                    onClick={() => togglePOComment(line.id)}
                                                                    title={expandedPOComments.has(line.id) ? "Collapse" : "Expand"}
                                                                >
                                                                    <svg 
                                                                        className={`comment-expand-arrow ${expandedPOComments.has(line.id) ? 'expanded' : ''}`}
                                                                        xmlns="http://www.w3.org/2000/svg" 
                                                                        width="12" 
                                                                        height="12" 
                                                                        viewBox="0 0 16 16" 
                                                                        fill="currentColor"
                                                                    >
                                                                        <path d="M8 11L3 6h10l-5 5z"/>
                                                                    </svg>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>}
                                                    {isColumnVisible('comments') && <td>
                                                        {line.comment ? (
                                                            // <HoverPO comments={line.comment || null}>
                                                            //     <textarea
                                                            //         value={
                                                            //             line.comment && line.comment.length > 0 && line.comment[line.comment.length - 1].comment
                                                            //                 ? line.comment[line.comment.length - 1].comment
                                                            //                 : ''
                                                            //         }
                                                            //         className="form-control"
                                                            //         rows={1}
                                                            //     />
                                                            // </HoverPO>

                                                            <div className="d-flex align-items-start gap-0">
                                                                <textarea style={{width:'180px'}}
                                                                    value={
                                                                        line.comment && line.comment.length > 0 && line.comment[line.comment.length - 1].comment
                                                                            ? line.comment[line.comment.length - 1].comment
                                                                            : ''
                                                                    }
                                                                    className="form-control"
                                                                    rows={1}
                                                                />

                                                                {/* <HoverPO comments={line.comment}>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-light"
                                                                        style={{ padding: "6px", borderRadius: "50%", border: "1px solid #ccc" }}
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 0 512 512">
                                                                            <path
                                                                                fill="currentColor"
                                                                                d="M344 0L488 0c13.3 0 24 10.7 24 24l0 144c0 9.7-5.8 18.5-14.8 22.2s-19.3 1.7-26.2-5.2l-39-39-87 87c-9.4 9.4-24.6 9.4-33.9 0l-32-32c-9.4-9.4-9.4-24.6 0-33.9l87-87L327 41c-6.9-6.9-8.9-17.2-5.2-26.2S334.3 0 344 0zM168 512L24 512c-13.3 0-24-10.7-24-24L0 344c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2l39 39 87-87c9.4-9.4 24.6-9.4 33.9 0l32 32c9.4 9.4 9.4 24.6 0 33.9l-87 87 39 39c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8z"
                                                                            />
                                                                        </svg>
                                                                    </button>
                                                                </HoverPO> */}
                                                                {renderNotesPresence(line.id)}
                                                                <Hover
                                                                poLineId = {line.id}
                                                                comments={[...(line.comment || []), ...(commentsMap[line.id] || [])]}
                                                                orderNumber={line.po?.ponum}
                                                                customerName={line.po?.vendorName}
                                                                lineInfo={line.linenum != null ? `Line ${line.linenum}` : undefined}
                                                                sourcePage="purchase"
                                                                onVisibleChange={(isVisible) => handleNotesVisibility(line.id, isVisible)}
                                                                onAddComment={(newComment, status) => handleAddComment(line.id, newComment, status)}
                                                                onDeleteComment={(commentId) => handleDeleteComment(line.id, commentId)}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-light"
                                                                    style={{ padding: "6px", borderRadius: "50%", border: "1px solid #ccc" }}
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 0 512 512">
                                                                        <path
                                                                            fill="currentColor"
                                                                            d="M344 0L488 0c13.3 0 24 10.7 24 24l0 144c0 9.7-5.8 18.5-14.8 22.2s-19.3 1.7-26.2-5.2l-39-39-87 87c-9.4 9.4-24.6 9.4-33.9 0l-32-32c-9.4-9.4-9.4-24.6 0-33.9l87-87L327 41c-6.9-6.9-8.9-17.2-5.2-26.2S334.3 0 344 0zM168 512L24 512c-13.3 0-24-10.7-24-24L0 344c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2l39 39 87-87c9.4-9.4 24.6-9.4 33.9 0l32 32c9.4 9.4 9.4 24.6 0 33.9l-87 87 39 39c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8z"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                            </Hover>
                                                            </div>
                                                        ) : (
                                                            <textarea className="form-control" rows={1} />
                                                        )}
                                                    </td>}
                                                    {isColumnVisible('line_status') && <td className="text-center" style={{ minWidth: '200px', width: '200px', maxWidth: '300px' }}>
                                                        <div className="line-status-dropdown-container position-relative" style={{ width: '100%' }} data-line-status-line-id={line.id}>
                                                            <div className="d-flex align-items-center gap-2" style={{ width: '100%' }}>
                                                                <div
                                                                    className="form-control d-flex align-items-start justify-content-between"
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        minHeight: "38px",
                                                                        backgroundColor: "white",
                                                                        border: "1px solid #ced4da",
                                                                        width: "100%",
                                                                        maxWidth: "100%",
                                                                        overflow: "visible",
                                                                        padding: "4px 8px"
                                                                    }}
                                                                    onClick={(e) => toggleLineStatusDropdown(line.id, e)}
                                                                >
                                                                    <div className="d-flex flex-wrap gap-1" style={{ width: "100%", alignContent: "flex-start" }}>
                                                                        {/* Current selections */}
                                                                        {(lineStatusSelections[line.id] || []).map((tag, index) => (
                                                                            <span
                                                                                key={`current-status-${index}`}
                                                                                className="badge"
                                                                                style={{
                                                                                    backgroundColor: tag.color,
                                                                                    color: "white",
                                                                                    fontSize: "0.75rem",
                                                                                    padding: "4px 8px",
                                                                                    borderRadius: "12px",
                                                                                    display: "inline-flex",
                                                                                    alignItems: "center",
                                                                                    gap: "4px"
                                                                                }}
                                                                            >
                                                                                {tag.label}
                                                                            </span>
                                                                        ))}

                                                                        {/* Submitted tags with click to deselect */}
                                                                        {(submittedLineStatuses[line.id] || []).map((tag, index) => {
                                                                            const isMarkedForDeselection = (lineStatusTagsToDeselect[line.id] || []).some(t => t.label === tag.label);
                                                                            return (
                                                                                <span
                                                                                    key={`submitted-status-${index}`}
                                                                                    className="badge"
                                                                                    style={{
                                                                                        backgroundColor: isMarkedForDeselection ? "#6c757d" : tag.color,
                                                                                        color: "white",
                                                                                        fontSize: "0.75rem",
                                                                                        padding: "4px 8px",
                                                                                        borderRadius: "12px",
                                                                                        cursor: "pointer",
                                                                                        opacity: isMarkedForDeselection ? 0.6 : 1,
                                                                                        display: "inline-flex",
                                                                                        alignItems: "center",
                                                                                        gap: "4px"
                                                                                    }}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleLineStatusDeselect(line.id, tag);
                                                                                    }}
                                                                                    title={isMarkedForDeselection ? "Click to keep this tag" : "Click to deselect this tag"}
                                                                                >
                                                                                    {tag.label}
                                                                                </span>
                                                                            );
                                                                        })}

                                                                        {(lineStatusSelections[line.id] || []).length === 0 && (submittedLineStatuses[line.id] || []).length === 0 && (
                                                                            <span className="text-muted">Select tags...</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {renderPoLinePresence(line.id)}
                                                                {hasLineStatusChangesToSubmit(line.id) && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-success btn-sm"
                                                                        style={{
                                                                            minWidth: "20px",
                                                                            height: "20px",
                                                                            padding: "0",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            fontSize: "10px",
                                                                            borderRadius: "2px"
                                                                        }}
                                                                        disabled={Boolean(poLineLockOwner(line.id))}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleLineStatusSubmit(line.id);
                                                                        }}
                                                                        title={
                                                                            poLineLockOwner(line.id)
                                                                                ? `${poLineLockOwner(line.id).name} is editing this line status`
                                                                                : "Submit changes"
                                                                        }
                                                                    >
                                                                        ✓
                                                                    </button>
                                                                )}
                                                            </div>

                                                            {/* Dropdown menu - only one open at a time */}
                                                            {lineStatusDropdownOpenId === line.id && lineStatusDropdownPosition && (
                                                                <div
                                                                    className="position-absolute"
                                                                    style={{
                                                                        top: `${lineStatusDropdownPosition.top}px`,
                                                                        left: `${lineStatusDropdownPosition.left}px`,
                                                                        width: `${lineStatusDropdownPosition.width}px`,
                                                                        zIndex: 1000,
                                                                        backgroundColor: 'white',
                                                                        border: '1px solid #ced4da',
                                                                        borderRadius: '4px',
                                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                                        maxHeight: '200px',
                                                                        overflowY: 'auto'
                                                                    }}
                                                                >
                                                                    {LINE_STATUS_TAGS.map((tag, tagIndex) => {
                                                                        const isSelected = (lineStatusSelections[line.id] || []).some(
                                                                            selection => selection.label === tag.label
                                                                        ) || (submittedLineStatuses[line.id] || []).some(
                                                                            selection => selection.label === tag.label
                                                                        );
                                                                        return (
                                                                            <div
                                                                                key={tagIndex}
                                                                                className="p-2 d-flex align-items-center"
                                                                                style={{
                                                                                    cursor: 'pointer',
                                                                                    backgroundColor: isSelected ? '#f8f9fa' : 'white',
                                                                                    borderBottom: '1px solid #f1f1f1'
                                                                                }}
                                                                                onClick={() => handleLineStatusToggle(line.id, tag)}
                                                                            >
                                                                                <div
                                                                                    className="me-2"
                                                                                    style={{
                                                                                        width: '12px',
                                                                                        height: '12px',
                                                                                        borderRadius: '2px',
                                                                                        backgroundColor: tag.color
                                                                                    }}
                                                                                ></div>
                                                                                <span style={{ fontSize: '0.875rem' }}>
                                                                                    {tag.label}
                                                                                </span>
                                                                                {isSelected && (
                                                                                    <span className="ms-auto">✓</span>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>}
                                                    {isColumnVisible('docs') && <td className="text-center production-docs-col">
                                                        <div className="d-flex flex-column align-items-center justify-content-center gap-2" style={{ maxWidth: '220px' }}>
                                                            {/* Display existing URLs as chips */}
                                                            {(poAccessUrls[line.id] || []).length > 0 && (
                                                                <div className="d-flex flex-wrap gap-1 justify-content-center" style={{ width: '100%' }}>
                                                                    {(poAccessUrls[line.id] || []).map((urlItem) => (
                                                                        <div
                                                                            key={`po-url-${urlItem.id}`}
                                                                            style={{
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                backgroundColor: '#f0f0f0',
                                                                                border: '1px solid #ddd',
                                                                                borderRadius: '12px',
                                                                                padding: '3px 6px',
                                                                                fontSize: '10px',
                                                                                maxWidth: '180px',
                                                                                position: 'relative'
                                                                            }}
                                                                        >
                                                                            <a
                                                                                href={urlItem.url}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{
                                                                                    textDecoration: 'none',
                                                                                    color: '#007bff',
                                                                                    fontWeight: '500',
                                                                                    marginRight: '4px',
                                                                                    flexGrow: 1,
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap'
                                                                                }}
                                                                                title={`${urlItem.urlName}: ${urlItem.url}`}
                                                                            >
                                                                                {urlItem.urlName}
                                                                            </a>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleDeletePoAccessUrl(line.id, urlItem.id, line.po?.id)}
                                                                                style={{
                                                                                    background: 'none',
                                                                                    border: 'none',
                                                                                    color: '#dc3545',
                                                                                    cursor: 'pointer',
                                                                                    padding: '0',
                                                                                    marginLeft: '2px',
                                                                                    fontSize: '12px',
                                                                                    lineHeight: '1',
                                                                                    width: '16px',
                                                                                    height: '16px',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center'
                                                                                }}
                                                                                title="Delete this URL"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {/* Add button */}
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                style={{
                                                                    fontSize: '0.7rem',
                                                                    padding: '3px 8px',
                                                                    height: '28px'
                                                                }}
                                                                onClick={() => handleDocsClick(line)}
                                                                title="Add URL link"
                                                            >
                                                                <i className="fa fa-plus" style={{ marginRight: '3px' }}></i>
                                                                Add
                                                            </button>
                                                        </div>
                                                    </td>}
                                                    {isColumnVisible('archive') && <td className="text-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0"
                                                            onClick={() => handleArchiveOrder(line)}
                                                            title="Archive this PO line"
                                                            style={{ 
                                                                fontSize: '1.2rem',
                                                                color: '#6c757d',
                                                                textDecoration: 'none'
                                                            }}
                                                        >
                                                            <i className="fa fa-paper-plane" aria-hidden="true"></i>
                                                        </button>
                                                    </td>}
                                                    {/* dangerouslySetInnerHTML={{ __html: line.po.comment?line.po.comment:'' }}></td> */}

                                                    {/* <td>{line.po.comment?line.po.comment:''}</td> */}
                                                    {/* <td className="shipping-comments-cell">
                                                                    <div className="shipping-comments-content"></div></td> */}
                                                    {/* <td style={{ textAlign: "center" }}>
                                                        <a
                                                            onClick={() => {
                                                                setUpdatePopUp(line);
                                                                handleShow();

                                                                const lastComment = line.comment?.[line.comment.length - 1] || null;
                                                                setFormData(lastComment);
                                                            }}
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            <img src={editIcon} className='img-fluid' style={{ height: "20px" }} />
                                                        </a>
                                                    </td> */}

                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={openPOColumns.filter(col => isColumnVisible(col.key)).length}>No data available</td>
                                        </tr>
                                    )}
                                </tbody>

                                {/* <tbody>
                                    {productionData.length > 0 ? (
                                        productionData.map((produ, Index) =>
                                            produ.po_lines
                                                // .filter((line) => line.status === statusFilter)
                                                .map((line, lineIndex) => {
                                                    const dueDate = new Date(line.duedate);
                                                    const today = new Date();
                                                    const timeDiff = dueDate - today;
                                                    const diffDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)); // Calculate days left


                                                    return (
                                                        <tr key={`${produ.id}-${lineIndex}`} {...getRowStyle(line.duedate)}>
                                                            
                                                            <td className='text-center'>
                                                                <a href={`https://gigaflightinc.cetecerp.com/purchaseorder/${produ.id}/view`} target='_blank' rel='noopener noreferrer'>
                                                                    {produ.ponum}
                                                                </a>
                                                            </td>
                                                            <td>{line.linenum}</td>
                                                            <td>{getVendorName(produ.vennum)}</td>
                                                            <td>{line.prcpart.substring(3) || 'N/A'}</td>
                                                            <td style={getDueDateStyle(line.duedate)}> {line.duedate} </td>
                                                           
                                                            <td className='text-center' style={{ color: diffDays < 0 ? 'red' : 'black' }}>
                                                                {diffDays}
                                                            </td>
                                                            <td>{line.status}</td>

                                                            <td className="text-center">
                                                                <select
                                                                    value={priorityMap[line.id] || 'Low'}
                                                                    onChange={(e) => handlePriorityChange(line.id, e.target.value)}
                                                                    className="form-select"
                                                                    style={{
                                                                        color: priorityMap[line.id] === 'High' ? 'red' :
                                                                            priorityMap[line.id] === 'Moderate' ? 'orange' :
                                                                                'green', //Ensure "Low" is green by default
                                                                        backgroundColor: 'white', //Always have white background when dropdown is closed
                                                                        borderColor: priorityMap[line.id] === 'High' ? 'red' :
                                                                            priorityMap[line.id] === 'Moderate' ? 'orange' :
                                                                                'green', //Ensure "Low" is green by default
                                                                        fontWeight: 'bold',
                                                                    }}
                                                                >
                                                                    <option value="Low" style={{ color: 'green' }}>Low</option>
                                                                    <option value="Moderate" style={{ color: 'orange' }}>Moderate</option>
                                                                    <option value="High" style={{ color: 'red' }}>High</option>
                                                                </select>
                                                            </td>

                                                            <td></td>
                                                        </tr>
                                                    );
                                                })
                                        )
                                    ) : (


                                        <tr>
                                            <td colSpan={openPOColumns.filter(col => isColumnVisible(col.key)).length} className="text-center">
                                                No Orders found
                                            </td>
                                        </tr>
                                    )}
                                </tbody> */}
                            </table>
                        )}
                    </div>
                </div>
                {/* <div className="col-12 py-2">
                    <nav aria-label="Page navigation example">
                        <span>
                            Showing {startRecord} to {endRecord} of {displayTotalRecords}
                        </span>
                        <ul className="pagination float-end">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
                            </li>
                            {[...Array(totalPages)].map((_, index) => (
                                <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                                    <button className="page-link" onClick={() => handlePageChange(index + 1)}>{index + 1}</button>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                            </li>
                        </ul>
                    </nav>
                </div> */}
                <div className="col-12 py-2">
                    <nav aria-label="Page navigation example">
                        <span>
                            Showing {totalRecords} of {displayTotalRecords}
                        </span>

                        <ul className="pagination float-end align-items-center mb-0">
                            {/* Previous Button */}
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                                    Previous
                                </button>
                            </li>

                            {/* Page Select Dropdown Styled Like Pagination */}
                            <li className="page-item">
                                <select
                                    className="form-select mx-2"
                                    style={{ width: 'auto' }}
                                    value={currentPage}
                                    onChange={(e) => handlePageChange(Number(e.target.value))}
                                >
                                    {[...Array(totalPages)].map((_, index) => (
                                        <option key={index} value={index + 1}>
                                            Page {index + 1}
                                        </option>
                                    ))}
                                </select>
                            </li>

                            {/* Next Button */}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                                    Next
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>

            </div>
            <Modal show={show} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group>
                            <Form.Label>Comment:</Form.Label>
                            <Form.Control
                                type="text"
                                name="comment"
                                value={formData ? formData.comment : ''}
                                onChange={handleChange}
                                placeholder="Enter comment"
                            />
                        </Form.Group>

                        <Form.Group className="mt-3">
                            <Form.Label>Flow Name:</Form.Label>
                            <Form.Control
                                type="text"
                                name="flowName"
                                value={"Order"}
                                onChange={handleChange}
                                disabled={true}
                                placeholder="Enter flow name"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mt-3">
                            <Form.Label>PO Status:</Form.Label>
                            <Form.Select
                                name="poStatus"
                                onChange={handleChange}
                            >
                            <option value="Open" selected={updatePopUp.status === 'Open'}>Open</option>
                            <option value="Closed" selected={updatePopUp.status === 'Closed'}>Closed</option>
                                   
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mt-3">
                            <Form.Label>Status:</Form.Label>
                            <Form.Select
                                name="status"
                                onChange={handleChange}
                                required
                            >
                                {formData ? (
                                    <>
                                        <option value="Can ship early" style={{ color: 'green' }} selected={formData.status === "Can ship early"}>Can ship early</option>
                                        <option value="Do not ship early" style={{ color: '#F8D700' }} selected={formData.status === "Do not ship early"}>Do not ship early</option>
                                        <option value="CC hold" style={{ color: 'orange' }} selected={formData.status === "CC hold"}>CC hold</option>
                                        <option value="Wire hold" style={{ color: '#8B47FF' }} selected={formData.status === "Wire hold"}>Wire hold</option>
                                        <option value="Credit hold" style={{ color: 'red' }} selected={formData.status === "Credit hold"}>Credit hold</option>
                                    </>
                                ) : (
                                    <>
                                        <option value="Can ship early" style={{ color: 'green' }}>Can ship early</option>
                                        <option value="Do not ship early" style={{ color: '#F8D700' }}>Do not ship early</option>
                                        <option value="CC hold" style={{ color: 'orange' }}>CC hold</option>
                                        <option value="Wire hold" style={{ color: '#8B47FF' }}>Wire hold</option>
                                        <option value="Credit hold" style={{ color: 'red' }}>Credit hold</option>
                                    </>
                                )}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mt-3">
                            <Form.Label>Order Line ID:</Form.Label>
                            <Form.Control
                                type="number"
                                name="orderLineId"
                                value={updatePopUp.id}
                                disabled={true}
                                onChange={handleChange}
                                placeholder="Enter Order Line ID"
                                required
                            />
                        </Form.Group>

                        {/* <Form.Group className="mt-3">
                                        <Form.Label>Last Update:</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={formData ?  formData.createdAt : ''}
                                            disabled={true}
                                            placeholder="Enter Order Line ID"
                                            required
                                        />
                                    </Form.Group> */}

                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleClose}>
                                Close
                            </Button>
                            <Button variant="primary" type="submit">
                                Save Changes
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Docs - Add PO Access URLs Modal */}
            <Modal show={showDocsModal} onHide={handleCloseDocsModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Manage PO URLs</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedLineForDocs && (
                        <div>
                            <div className="mb-3">
                                <p className="mb-0">
                                    <strong>PO:</strong> {selectedLineForDocs.po?.ponum ?? 'N/A'} &nbsp;|&nbsp; <strong>Line#:</strong> {selectedLineForDocs.linenum ?? 'N/A'}
                                </p>
                            </div>

                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>URL Name:</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={newPoAccessUrlName}
                                        onChange={(e) => setNewPoAccessUrlName(e.target.value)}
                                        placeholder="e.g., Engineering Docs, Vendor Files, etc."
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>URL:</Form.Label>
                                    <Form.Control
                                        type="url"
                                        value={newPoAccessUrl}
                                        onChange={(e) => setNewPoAccessUrl(e.target.value)}
                                        placeholder="https://example.com/folder"
                                    />
                                </Form.Group>
                            </Form>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDocsModal}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleAddPoAccessUrl}>
                        Add URL
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Column Visibility Modal */}
            <ColumnVisibilityModal
                show={showColumnModal}
                onHide={() => setShowColumnModal(false)}
                columns={openPOColumns}
                visibleColumns={columnVisibility}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                pageName="openPO"
            />
        </div>
    );
};



export default Production;